import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './MessagesTab.module.css';

// ✅ ADD THIS VALIDATION FUNCTION - Near the top of MessagesTab.jsx file

/**
 * ✅ MEDIA LIMIT VALIDATOR
 * Validates that uploaded media doesn't exceed:
 * - Maximum 4 videos
 * - Maximum 10 images
 */
const validateMediaLimits = (existingMedia, newFiles) => {
  // Separate existing media by type
  const existingVideos = existingMedia.filter(m => m.type === 'video');
  const existingImages = existingMedia.filter(m => m.type === 'image');
  
  // Separate new files by type
  const newVideos = Array.from(newFiles).filter(f => f.type.startsWith('video/'));
  const newImages = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
  
  // Check video limit (max 4)
  const totalVideos = existingVideos.length + newVideos.length;
  if (totalVideos > 4) {
    const excess = totalVideos - 4;
    return {
      valid: false,
      error: `❌ Video limit exceeded! Maximum 4 videos allowed. You're trying to add ${excess} extra video(s).`,
      type: 'video',
      count: totalVideos,
      max: 4,
    };
  }
  
  // Check image limit (max 10)
  const totalImages = existingImages.length + newImages.length;
  if (totalImages > 10) {
    const excess = totalImages - 10;
    return {
      valid: false,
      error: `❌ Image limit exceeded! Maximum 10 images allowed. You're trying to add ${excess} extra image(s).`,
      type: 'image',
      count: totalImages,
      max: 10,
    };
  }
  
  return {
    valid: true,
    error: null,
    videosRemaining: 4 - existingVideos.length,
    imagesRemaining: 10 - existingImages.length,
    videoCount: existingVideos.length,
    imageCount: existingImages.length,
  };
};


const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

function formatTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatChatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatChatDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    month: 'long', day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function timeUntilExpiry(expiresAt) {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

function getInitials(str) {
  if (!str) return '?';
  return str.charAt(0).toUpperCase();
}

// Mask an email used as a display name so the seller can't see the exact email
// of the person who liked their listing.
// e.g. "gordonkimz280@gmail.com" → "gordonkimz"   (strips trailing digits + @domain)
//      "jane.doe@mail.com"       → "jane.doe"
// Non-email values (real usernames) are returned as-is.
function maskLikerEmail(raw) {
  if (!raw) return 'Someone';
  if (!raw.includes('@')) return raw; // already a real username — show as-is
  const localPart = raw.split('@')[0];          // everything before @
  return localPart.replace(/\d+$/, '') || 'Someone'; // strip trailing digits
}

function getStatusColor(status) {
  switch (status) {
    case 'accepted':  return '#22c55e';
    case 'rejected':  return '#ef4444';
    case 'countered': return '#f59e0b';
    case 'withdrawn': return '#94a3b8';
    default:          return '#3b82f6';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'pending':   return 'Pending';
    case 'accepted':  return 'Accepted ✓';
    case 'rejected':  return 'Declined';
    case 'countered': return 'Countered';
    case 'withdrawn': return 'Withdrawn';
    default:          return status;
  }
}

const AVATAR_COLORS = ['#25D366','#128C7E','#075E54','#34B7F1','#e67e22','#8e44ad','#2980b9','#16a085'];

function avatarColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 46 }) {
  return (
    <div className={styles.avatar} style={{ width: size, height: size, background: avatarColor(name), fontSize: size * 0.4 }}>
      {getInitials(name)}
    </div>
  );
}

// ✅ Resolve any media URL to a usable absolute URL.
// Handles: relative paths, 127.0.0.1 dev URLs, already-correct URLs.
const BACKEND_HOST = 'https://imselling-marketplace-production.up.railway.app';
function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  // Already absolute with correct host
  if (url.startsWith('http://192.168.56.1') || url.startsWith('https://')) return url;
  // data: or blob: — pass through
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  // Dev server URL — swap host
  if (url.startsWith('http://127.0.0.1') || url.startsWith('http://localhost')) {
    return url.replace(/^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?/, BACKEND_HOST);
  }
  // Relative URL (e.g. /media/statuses/foo.jpg)
  if (url.startsWith('/')) return `${BACKEND_HOST}${url}`;
  // Relative without leading slash
  return `${BACKEND_HOST}/${url}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIDEO THUMB — shows video content in the circle avatar.
// For cross-origin video where autoPlay is blocked, shows a coloured gradient
// with a play icon so it's clear it's a video status, not a broken image.
// When thumbnail_url is provided by the backend (generated by ffmpeg),
// it will be shown as a static <img> instead and this component won't be called.
// ═══════════════════════════════════════════════════════════════════════════════
function VideoThumb({ src, style, fallback, userName }) {
  // ✅ FIXED: Removed opacity:0/onPlaying trick — cross-origin autoPlay is blocked
  // by browsers so onPlaying never fires, leaving the circle black forever.
  // New approach: fallback sits underneath (zIndex 1), video renders on top
  // (zIndex 2) WITHOUT hiding. The browser paints the first decoded frame
  // even when it blocks autoPlay animation — so we get a still preview.
  // If the video truly fails (network error), fallback shows through.
  const [errored, setErrored] = React.useState(false);

  if (!src || errored) return fallback || null;

  return (
    <div style={{ position: 'relative', width: style?.width || '100%', height: style?.height || '100%' }}>
      {/* Fallback layer — shows initials until/unless video paints */}
      {fallback && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          {fallback}
        </div>
      )}
      {/* Video on top — first frame renders even when autoPlay is blocked */}
      <video
        src={src}
        muted
        autoPlay
        playsInline
        loop
        preload="auto"
        style={{
          ...style,
          display: 'block',
          objectFit: 'cover',
          position: 'absolute',
          top: 0, left: 0,
          zIndex: 2,
        }}
        onTimeUpdate={e => {
          if (e.target.currentTime >= 10) e.target.currentTime = 0;
        }}
        onError={() => setErrored(true)}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SEGMENTED RING — WhatsApp-style split circle showing N story segments
// ═══════════════════════════════════════════════════════════════════════════════
function SegmentedRing({ count = 1, viewed = 0, size = 68, strokeWidth = 3 }) {
  const r    = (size - strokeWidth) / 2;
  const cx   = size / 2;
  const cy   = size / 2;
  const circ = 2 * Math.PI * r;

  if (count === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#d1d5db" strokeWidth={strokeWidth} strokeLinecap="round" />
      </svg>
    );
  }

  const gapLen = count > 1 ? Math.min(3.5, circ / (count * 4)) : 0;
  const segLen = count > 1 ? (circ - gapLen * count) / count : circ;

  const segments = Array.from({ length: count }, (_, i) => {
    const isViewed = i < viewed;
    const offset   = circ / 4 - i * (segLen + gapLen);
    return (
      <circle
        key={i}
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke={isViewed ? '#c5cdd3' : '#25D366'}
        strokeWidth={strokeWidth}
        strokeDasharray={`${segLen} ${circ - segLen}`}
        strokeDashoffset={offset}
        strokeLinecap={count === 1 ? 'round' : 'butt'}
      />
    );
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      {segments}
    </svg>
  );
}

// ── Strips __SREPLY__ metadata tag → returns human-readable preview text ──────
// Used wherever raw message text is displayed as a short preview (inbox row,
// notification, etc.) instead of as a full rendered bubble.
function stripStatusReply(msg) {
  if (!msg || !msg.startsWith('__SREPLY__')) return msg;
  try {
    const SREPLY_RE = /^__SREPLY__(\{.*?\})__\n?([\s\S]*)/;
    const match = msg.match(SREPLY_RE);
    if (!match) return msg;
    const meta    = JSON.parse(match[1]);
    const userTxt = (match[2] || '').trim();
    const icon    = meta.type === 'video' ? '🎥' : '🖼';
    const label   = meta.caption
      ? `${icon} ${meta.caption.slice(0, 40)}${meta.caption.length > 40 ? '…' : ''}`
      : `${icon} ${meta.type === 'video' ? 'Video' : 'Photo'}`;
    return userTxt ? `${userTxt} · ${label}` : label;
  } catch {
    return msg.replace(/__SREPLY__[\s\S]*?__\n?/, '').trim() || '📎 Status reply';
  }
}

function isMessageFromCurrentUser(message, currentUserEmail, conversationContext) {
  const senderEmail    = (message.sender?.email    || message.from_email    || '').toLowerCase().trim();
  const senderUsername = (message.sender?.username || message.from_username || '').toLowerCase().trim();
  const currentEmail   = currentUserEmail.toLowerCase().trim();
  if (senderEmail && senderEmail === currentEmail) return true;
  if (conversationContext) {
    const otherEmail    = (conversationContext.otherEmail    || '').toLowerCase().trim();
    const otherUsername = (conversationContext.otherUsername || '').toLowerCase().trim();
    if (senderEmail    && senderEmail    === otherEmail)    return false;
    if (senderUsername && senderUsername === otherUsername) return false;
    if (otherEmail && senderEmail && senderEmail !== otherEmail) return true;
  }
  if (message.sender?.is_me !== undefined) return message.sender.is_me;
  return true;
}

function FlaggedBanner() {
  return (
    <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #1a0505, #2d0a0a)', borderTop: '2px solid #7f1d1d', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '1.5px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🚩</div>
      <div style={{ flex: 1 }}>
        <div style={{ color: '#fca5a5', fontWeight: 700, fontSize: 13.5, marginBottom: 3 }}>This conversation has been flagged</div>
        <div style={{ color: 'rgba(252,165,165,0.55)', fontSize: 11.5, lineHeight: 1.45 }}>Messaging is disabled. An admin is reviewing this conversation.</div>
      </div>
      <div style={{ padding: '5px 11px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 20, color: '#f87171', fontSize: 10, fontWeight: 800, letterSpacing: '0.4px', whiteSpace: 'nowrap', flexShrink: 0 }}>FLAGGED</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS VIEWER
// ✅ FIX 1: Progress bar zIndex 6 (was 4 — same as top bar = invisible)
// ✅ FIX 2: Top bar zIndex 5 (was 4 — now above media, below progress bar)
// ✅ FIX 3: current.is_viewed mutated in-place → ring turns grey on close instantly
// ═══════════════════════════════════════════════════════════════════════════════
function StatusViewer({ group, currentUserEmail, onClose, onDeleted, onViewed }) {
  const [idx, setIdx]             = useState(0);
  const [progress, setProgress]   = useState(0);
  const [paused, setPaused]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [viewedSet, setViewedSet] = useState(new Set());
  // ✅ Reply & like state
  const [replyText, setReplyText] = useState('');
  const [liked,     setLiked]     = useState(false);
  const [likeAnim,  setLikeAnim]  = useState(false);
  const [sending,   setSending]   = useState(false);
  const [sentMsg,   setSentMsg]   = useState('');
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const inputRef = useRef(null);

  const statuses = group.statuses;
  const current  = statuses[idx];
  const isOwn =
    (group.user.email    || '').toLowerCase() === currentUserEmail.toLowerCase() ||
    (group.user.username || '').toLowerCase() === currentUserEmail.toLowerCase();

  // ✅ BUG 2 FIX: Dynamic duration — reads actual video length from the element.
  // Fallback: 15s for video, 5s for image, until video metadata loads.
  const [duration, setDuration] = useState(
    current?.media_type === 'video' ? 15000 : 5000
  );
  // Alias so the rest of the component uses DURATION unchanged
  const DURATION = duration;

  // ✅ BUG 2: Reset duration to default when switching to a new status item
  useEffect(() => {
    if (!current) return;
    setDuration(current.media_type === 'video' ? 15000 : 5000);
    setReplyText(''); setLiked(false); setSentMsg('');
  }, [idx]);

  // ✅ FIX 3: Mark viewed locally AND on backend. Mutate current.is_viewed in-place
  //    so SegmentedRing shows grey immediately when viewer closes — no re-fetch wait.
  useEffect(() => {
    if (!current) return;
    setViewedSet(prev => new Set([...prev, current.id]));
    if (isOwn) return;
    fetch(`${API_BASE}/statuses/${current.id}/view/`, {
      method: 'POST',
      headers: { Authorization: `Token ${getToken()}` },
    })
      .then(() => {
        current.is_viewed = true;
        onViewed?.(current.id); // ✅ BUG 3 FIX: notify parent to update ring immediately
      })
      .catch(() => {
        current.is_viewed = true;
        onViewed?.(current.id); // ✅ also update on failure (mark locally)
      });
  }, [current?.id, isOwn]);

  // ── Progress timer ──────────────────────────────────────────────────────────
  // Split into TWO effects:
  //   Effect A: only fires when the STATUS CHANGES (idx / DURATION).
  //             Resets progress to 0 and starts a fresh interval.
  //   Effect B: only fires when PAUSED changes.
  //             Clears or restarts the interval WITHOUT resetting progress,
  //             so resume continues from exactly where it stopped.

  // Effect A — new status: reset progress + start interval
  useEffect(() => {
    clearInterval(timerRef.current);
    setProgress(0);
    if (paused) return; // if we arrive already paused, don't start yet
    const TICK = 50;
    const increment = (TICK / DURATION) * 100;
    timerRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (idx < statuses.length - 1) { setIdx(i => i + 1); return 0; }
          else { onClose(); return 0; }
        }
        return prev + increment;
      });
    }, TICK);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, DURATION]); // ← intentionally excludes `paused` so this never re-runs on pause

  // Effect B — pause / resume: clear or restart interval WITHOUT touching progress
  useEffect(() => {
    if (paused) {
      // Pausing — just stop the interval, keep progress where it is
      clearInterval(timerRef.current);
      timerRef.current = null;
    } else {
      // Resuming — restart interval from current progress (not from 0)
      clearInterval(timerRef.current);
      const TICK = 50;
      const increment = (TICK / DURATION) * 100;
      timerRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            if (idx < statuses.length - 1) { setIdx(i => i + 1); return 0; }
            else { onClose(); return 0; }
          }
          return prev + increment;
        });
      }, TICK);
    }
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]); // ← only fires on pause toggle, never resets progress

  // ✅ Send reply with embedded status snippet metadata
  // Format: __SREPLY__{json}__\nUserText
  // The MessageBubble component detects this tag and renders a visual snippet.
  const handleSendReply = async (textOverride) => {
    const text = (textOverride || replyText).trim();
    if (!text || sending) return;
    setSending(true);
    setPaused(true);
    try {
      // ── Build embedded metadata tag ──────────────────────────────────
      // thumbnail_url = pre-generated still (best).
      // For images: fall back to media_url directly (it IS the image).
      // For videos without thumbnail: pass media_url as videoSrc so the
      // bubble can render a <video> element to show the first frame.
      const isVideo   = current.media_type === 'video';
      const thumbSrc  = resolveMediaUrl(
        current.thumbnail_url ||
        (!isVideo ? current.media_url : null)
      );
      const videoSrc  = (isVideo && !current.thumbnail_url)
        ? resolveMediaUrl(current.media_url) : '';
      const meta = JSON.stringify({
        thumb:    thumbSrc  || '',
        videoSrc: videoSrc  || '',      // used when no thumbnail available
        type:     current.media_type || 'image',
        caption:  current.caption   || '',
        poster:   group.user.username || group.user.email || '',
      });
      // Full message = metadata tag + newline + user text
      const fullMsg = `__SREPLY__${meta}__\n${text}`;

      // ── POST to /api/messages/direct/ ────────────────────────────────
      const res = await fetch(`${API_BASE}/messages/direct/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: group.user.id,
          message:      fullMsg,
          status_id:    current.id,
        }),
      });

      if (!res.ok) {
        // Fallback: find poster's latest ad and use ad_messages endpoint
        const adsRes = await fetch(
          `${API_BASE}/ads/?user=${encodeURIComponent(group.user.email || group.user.username)}&page_size=1`,
          { headers: { Authorization: `Token ${getToken()}` } }
        );
        if (adsRes.ok) {
          const adsData = await adsRes.json();
          const adId = adsData.results?.[0]?.id || adsData[0]?.id;
          if (adId) {
            await fetch(`${API_BASE}/ads/${adId}/messages/`, {
              method: 'POST',
              headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: fullMsg }),
            });
          } else throw new Error('No ad found');
        } else throw new Error('Fallback failed');
      }

      setReplyText('');
      setSentMsg('✓ Sent!');
      setTimeout(() => { setSentMsg(''); setPaused(false); }, 2000);
    } catch (err) {
      console.error('Status reply error:', err);
      setSentMsg('✗ Failed');
      setTimeout(() => { setSentMsg(''); setPaused(false); }, 2000);
    } finally { setSending(false); }
  };

  // ✅ Like — triggers ❤️ animation + sends heart reply
  const handleLike = async () => {
    if (liked || isOwn) return;
    setLiked(true); setLikeAnim(true);
    setTimeout(() => setLikeAnim(false), 700);
    try {
      await fetch(`${API_BASE}/statuses/${current.id}/like/`, {
        method: 'POST', headers: { Authorization: `Token ${getToken()}` },
      });
      await handleSendReply('❤️');
    } catch { /* non-fatal */ }
  };

  const handlePauseToggle = () => {
    setPaused(p => {
      const next = !p;
      if (videoRef.current) {
        if (next) {
          videoRef.current.pause();
        } else {
          // ✅ Before resuming, sync progress bar to video's actual currentTime
          // This prevents drift when the video was seeked or buffered differently
          const vid = videoRef.current;
          if (vid.duration > 0) {
            const syncedProgress = (vid.currentTime / vid.duration) * 100;
            setProgress(syncedProgress);
          }
          vid.play().catch(() => {});
        }
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this status?')) return;
    setDeleting(true);
    try {
      await fetch(`${API_BASE}/statuses/${current.id}/`, {
        method: 'DELETE',
        headers: { Authorization: `Token ${getToken()}` },
      });
      onDeleted?.();
      if (idx < statuses.length - 1) setIdx(i => i + 1);
      else onClose();
    } finally {
      setDeleting(false);
    }
  };

  if (!current) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#e8eaed', zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>

      {/* ── ✅ FIX 1: Progress bars zIndex 6 — above top bar (5) ── */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        zIndex: 6,
        display: 'flex', gap: 3, padding: '10px 12px 0',
      }}>
        {statuses.map((s, i) => {
          const isPastStatus       = i < idx;
          const isCurrentAndViewed = i === idx && (viewedSet.has(s.id) || s.is_viewed);
          const isViewed           = isPastStatus || isCurrentAndViewed;

          let barWidth = '0%';
          if (i < idx)        barWidth = '100%';
          else if (i === idx) barWidth = `${progress}%`;

          // ✅ BUG 1 FIX: Current bar (i===idx) is ALWAYS green while playing.
          // Past bars (i < idx) are grey — they've been fully viewed.
          // Future bars (i > idx) are empty/transparent.
          // viewedSet must NOT affect the CURRENT bar's colour —
          // we add current.id to viewedSet immediately on mount which
          // was incorrectly turning the active bar grey.
          const barColor = i < idx ? '#c5cdd3'    // past = grey
                         : i === idx ? '#25D366'   // current = always green
                         : 'transparent';          // future = empty

          return (
            <div key={i} style={{
              flex: 1, height: 3,
              background: 'rgba(0,0,0,0.25)',
              borderRadius: 2, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                borderRadius: 2,
                background: barColor,
                width: barWidth,
                transition: i === idx ? 'none' : 'width 0.3s ease',
              }} />
            </div>
          );
        })}
      </div>

      {/* ── ✅ FIX 2: Top bar zIndex 5 — above media, below progress bars ── */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '32px 16px 10px',
      }}>
        <Avatar name={group.user.username} size={36} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#111', fontWeight: 700, fontSize: 14 }}>{group.user.username}</div>
          <div style={{ color: '#555', fontSize: 11 }}>{timeUntilExpiry(current.expires_at)}</div>
        </div>

        <button
          onClick={handlePauseToggle}
          title={paused ? 'Resume' : 'Pause'}
          style={{
            background: 'rgba(0,0,0,0.1)', border: '1.5px solid rgba(0,0,0,0.15)',
            borderRadius: '50%', width: 34, height: 34, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {paused
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="#111"><path d="M8 5v14l11-7z"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="#111"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          }
        </button>

        {isOwn && (
          <button onClick={handleDelete} disabled={deleting} style={{
            background: 'rgba(239,68,68,0.12)', border: '1.5px solid rgba(239,68,68,0.3)',
            borderRadius: 8, color: '#dc2626', padding: '6px 12px',
            fontSize: 12, cursor: 'pointer', fontWeight: 600, flexShrink: 0,
          }}>
            🗑 Delete
          </button>
        )}

        <button onClick={onClose} title="Close" style={{
          background: 'rgba(0,0,0,0.1)', border: '1.5px solid rgba(0,0,0,0.15)',
          borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111',
          flexShrink: 0, zIndex: 5, position: 'relative',
        }}>×</button>
      </div>

      {/* ── Media area — NO tap zones, ONLY arrow buttons navigate ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>

        <div style={{ position: 'relative', height: '100vh', maxWidth: 480, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a' }}>
          {(() => {
            // ✅ Always resolve URLs — backend returns http://127.0.0.1:8000/...
            // but the browser may be on a different network (192.168.56.1).
            const resolvedSrc = resolveMediaUrl(current.media_url);
            return current.media_type === 'video'
              ? <video
                  ref={videoRef}
                  key={current.id}
                  src={resolvedSrc}
                  autoPlay
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onLoadedMetadata={e => {
                    const actualMs = e.target.duration * 1000;
                    if (actualMs > 0) setDuration(actualMs);
                  }}
                />
              : <img
                  key={current.id}
                  src={resolvedSrc}
                  alt="Status"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />;
          })()}

          {/* ✅ CENTERED caption pill */}
          {current.caption && (
            <div style={{
              position: 'absolute', bottom: isOwn ? '60px' : '82px',
              left: 0, right: 0,
              display: 'flex', justifyContent: 'center', padding: '0 20px',
              pointerEvents: 'none', zIndex: 4,
            }}>
              <div style={{
                background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
                color: '#fff', fontSize: 16, fontWeight: 500, lineHeight: 1.55,
                padding: '10px 20px', borderRadius: 14, textAlign: 'center',
                maxWidth: 360, boxShadow: '0 2px 14px rgba(0,0,0,0.45)',
              }}>
                {current.caption}
              </div>
            </div>
          )}

          {isOwn && (
            <div style={{ position: 'absolute', bottom: 20, right: 16, color: 'rgba(255,255,255,0.8)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.35)', borderRadius: 20, padding: '3px 10px', zIndex: 4 }}>
              👁 {current.view_count ?? 0} view{(current.view_count ?? 0) !== 1 ? 's' : ''}
            </div>
          )}
          {/* ✅ ❤️ like animation overlay */}
          {likeAnim && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10 }}>
              <div style={{ fontSize: 80, lineHeight: 1, animation: 'statusLikePop 0.7s ease-out forwards' }}>❤️</div>
            </div>
          )}

          {paused && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ background: 'rgba(0,0,0,0.45)', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              </div>
            </div>
          )}
        </div>

        {/* ‹ Previous */}
        <button
          onClick={() => { if (idx > 0) { setIdx(i => i - 1); setProgress(0); } }}
          disabled={idx === 0}
          title="Previous"
          style={{
            position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
            background: idx === 0 ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.18)',
            border: '1.5px solid rgba(0,0,0,0.15)',
            borderRadius: '50%', width: 48, height: 48,
            cursor: idx === 0 ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: idx === 0 ? 0.3 : 1, transition: 'all 0.15s', zIndex: 3,
          }}
          onMouseEnter={e => { if (idx > 0) e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = idx === 0 ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.18)'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        </button>

        {/* › Next */}
        <button
          onClick={() => { if (idx < statuses.length - 1) { setIdx(i => i + 1); setProgress(0); } else onClose(); }}
          title={idx < statuses.length - 1 ? 'Next' : 'Close'}
          style={{
            position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.18)', border: '1.5px solid rgba(0,0,0,0.15)',
            borderRadius: '50%', width: 48, height: 48, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s', zIndex: 3,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.18)'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

      {/* ✅ Reply bar + Like button — only for other people's statuses */}
      {!isOwn && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 7,
          padding: '10px 14px 16px',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.72))',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center',
            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(8px)',
            border: '1.5px solid rgba(255,255,255,0.22)',
            borderRadius: 26, padding: '0 12px',
          }}>
            <input
              ref={inputRef}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onFocus={() => setPaused(true)}
              onBlur={() => { if (!replyText.trim()) setPaused(false); }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              placeholder={sentMsg || `Reply to ${group.user.username}…`}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: sentMsg ? (sentMsg.startsWith('✓') ? '#4ade80' : '#f87171') : '#fff',
                fontSize: 14, padding: '11px 0', fontFamily: 'inherit',
              }}
            />
            {replyText.trim() && (
              <button onClick={() => handleSendReply()} disabled={sending} style={{
                background: '#25D366', border: 'none', borderRadius: '50%',
                width: 32, height: 32, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginLeft: 6,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            )}
          </div>
          <button onClick={handleLike} disabled={liked} title={liked ? 'Liked!' : 'Like'}
            style={{
              background: liked ? 'rgba(239,68,68,0.28)' : 'rgba(255,255,255,0.14)',
              backdropFilter: 'blur(8px)',
              border: `1.5px solid ${liked ? 'rgba(239,68,68,0.65)' : 'rgba(255,255,255,0.22)'}`,
              borderRadius: '50%', width: 46, height: 46, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: liked ? 'default' : 'pointer', fontSize: 22, transition: 'all 0.2s',
              transform: likeAnim ? 'scale(1.35)' : 'scale(1)',
            }}
          >{liked ? '❤️' : '🤍'}</button>
        </div>
      )}

      <style>{`
        @keyframes statusLikePop {
          0%   { transform: scale(0.4); opacity: 1; }
          45%  { transform: scale(1.5); opacity: 1; }
          100% { transform: scale(1.1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS UPLOADER MODAL
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// STATUS UPLOADER MODAL - WITH MEDIA LIMIT VALIDATION
// ✅ NEW: Validates max 4 videos, 10 images before upload
// ═══════════════════════════════════════════════════════════════════════════════
function StatusUploader({ 
  onClose, 
  onUploaded,
  // ✅ NEW: Accept these props from parent
  statusMedia = [],
  setStatusMedia = () => {},
  mediaValidation = {},
  setMediaValidation = () => {},
}) {
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [caption, setCaption]     = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [dragOver, setDragOver]   = useState(false);
  const fileRef = useRef();

  const existingVideos = mediaValidation.videoCount || 0;
  const existingImages = mediaValidation.imageCount || 0;
  const selectedIsVideo = !!file?.type?.startsWith('video/');
  const selectedIsImage = !!file?.type?.startsWith('image/');
  const displayVideoCount = existingVideos + (selectedIsVideo ? 1 : 0);
  const displayImageCount = existingImages + (selectedIsImage ? 1 : 0);
  const videosRemaining = Math.max(0, 4 - displayVideoCount);
  const imagesRemaining = Math.max(0, 10 - displayImageCount);

  const clearSelectedFile = () => {
    setFile(null);
    setPreview(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ STEP 1: processFile - Validate file size + MEDIA LIMITS
  // Counts stay database-driven; only the current pending file affects display.
  // ════════════════════════════════════════════════════════════════════════════════
  const processFile = (f) => {
    if (!f) return;

    const isVideo = f.type.startsWith('video/');
    const isImage = f.type.startsWith('image/');

    if (!isVideo && !isImage) {
      setError('❌ Please select an image or video file.');
      return;
    }

    const maxMB = isVideo ? 50 : 10;
    if (f.size > maxMB * 1024 * 1024) {
      setError(`❌ File too large. Max ${maxMB}MB for ${isVideo ? 'videos' : 'images'}.`);
      return;
    }

    if (isVideo && existingVideos >= 4) {
      const errorMsg = `❌ Video limit reached! You can only upload maximum 4 videos. (${existingVideos}/4 already uploaded)`;
      setError(errorMsg);
      setMediaValidation(prev => ({ ...prev, error: errorMsg }));
      return;
    }

    if (isImage && existingImages >= 10) {
      const errorMsg = `❌ Image limit reached! You can only upload maximum 10 images. (${existingImages}/10 already uploaded)`;
      setError(errorMsg);
      setMediaValidation(prev => ({ ...prev, error: errorMsg }));
      return;
    }

    setError('');
    setMediaValidation(prev => ({ ...prev, error: null }));
    setFile(f);
    setMediaType(isVideo ? 'video' : 'image');
    setPreview(URL.createObjectURL(f));
  };

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ STEP 5: handleUpload - Final validation before POST
  // Uses counts already derived from database records.
  // ════════════════════════════════════════════════════════════════════════════════
  const handleUpload = async () => {
    if (!file) {
      fileRef.current?.click();
      return;
    }

    const isCurrentVideo = file.type.startsWith('video/');
    const isCurrentImage = file.type.startsWith('image/');

    if (isCurrentVideo && existingVideos >= 4) {
      setError('❌ Cannot upload - video limit already reached (4/4)');
      return;
    }

    if (isCurrentImage && existingImages >= 10) {
      setError('❌ Cannot upload - image limit already reached (10/10)');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const fd = new FormData();
      fd.append('media', file);
      fd.append('caption', caption.trim());

      const res = await fetch(`${API_BASE}/statuses/`, {
        method: 'POST',
        headers: { Authorization: `Token ${getToken()}` },
        body: fd,
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      onUploaded?.(data.status);
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload status');
    } finally {
      setUploading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ STEP 6: handleRemoveMedia - Remove from queue
  // Queue UI stays intact, but media limits are no longer recalculated locally.
  // ════════════════════════════════════════════════════════════════════════════════
  const handleRemoveMedia = (id) => {
    const updated = statusMedia.filter(m => m.id !== id);
    setStatusMedia(updated);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#1a2332', borderRadius: 22, width: 380, maxWidth: '95vw', padding: 28, boxShadow: '0 32px 96px rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.07)' }}>
        
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* HEADER */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <h3 style={{ margin: 0, color: '#fff', fontSize: 19, fontWeight: 800 }}>Add Status</h3>
            <p style={{ margin: '3px 0 0', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Visible for 24 hours</p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', color: 'rgba(255,255,255,0.6)', width: 34, height: 34, fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* ✅ NEW: Show media counts and limits */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {statusMedia.length > 0 && (
          <div style={{
            background: 'rgba(37,211,102,0.12)',
            border: '1px solid rgba(37,211,102,0.3)',
            borderRadius: 10,
            padding: 12,
            marginBottom: 14,
            fontSize: 12,
            color: '#25D366',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>📊 <strong>Media Queue:</strong></div>
            <div>
              🎬 {displayVideoCount}/4 | 🖼️ {displayImageCount}/10
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* ✅ NEW: Show uploaded media list with remove buttons */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {statusMedia.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10,
            maxHeight: 120,
            overflowY: 'auto',
            marginBottom: 14,
            padding: 8
          }}>
            {statusMedia.map((media, idx) => (
              <div key={media.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 6,
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 6,
                marginBottom: idx < statusMedia.length - 1 ? 6 : 0,
                fontSize: 12,
                color: '#fff'
              }}>
                <span>
                  {media.type === 'video' ? '🎬' : '🖼️'} {media.file.name.length > 20 ? media.file.name.slice(0, 20) + '...' : media.file.name}
                </span>
                <button
                  onClick={() => handleRemoveMedia(media.id)}
                  style={{
                    marginLeft: 'auto',
                    background: 'rgba(239,68,68,0.3)',
                    border: 'none',
                    color: '#fca5a5',
                    padding: '2px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600
                  }}
                >
                  ✕ Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* PREVIEW / FILE SELECT */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {preview ? (
          <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#000', height: 220, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {mediaType === 'video' ? (
              <video src={preview} controls muted style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }} />
            ) : (
              <img src={preview} alt="Preview" style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }} />
            )}
            <button onClick={clearSelectedFile} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', color: '#fff', width: 30, height: 30, fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            <button onClick={() => fileRef.current?.click()} style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(37,211,102,0.85)', border: 'none', borderRadius: 8, color: '#fff', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Change</button>
          </div>
        ) : (
          <div onClick={() => fileRef.current?.click()} onDrop={e => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files[0]); }} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
            style={{ height: 190, border: `2px dashed ${dragOver ? '#25D366' : 'rgba(255,255,255,0.13)'}`, borderRadius: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 16, gap: 8, background: dragOver ? 'rgba(37,211,102,0.06)' : 'rgba(255,255,255,0.03)', transition: 'all 0.2s' }}>
            <div style={{ fontSize: 44, lineHeight: 1 }}>📷</div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, fontWeight: 600 }}>Tap to choose photo or video</div>
            <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>or drag &amp; drop here</div>
            <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>Images ≤ 10 MB · Videos ≤ 50 MB</div>
            
            {/* ✅ NEW: Show remaining limits */}
            <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,200,0,0.8)', textAlign: 'center', lineHeight: 1.4 }}>
              ⚠️ Remaining: <strong>{videosRemaining} videos</strong> · <strong>{imagesRemaining} images</strong>
            </div>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* CAPTION INPUT */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <input type="text" placeholder="Add a caption… (optional)" value={caption} onChange={e => setCaption(e.target.value.slice(0, 500))}
            style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 11, padding: '11px 46px 11px 14px', color: '#fff', fontSize: 14, outline: 'none' }} />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>{caption.length}/500</span>
        </div>

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* ✅ NEW: Show validation error messages */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.13)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 9, padding: '9px 13px', color: '#fca5a5', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* MEDIA TYPE SELECTOR */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[['🖼 Image','image'],['🎥 Video','video']].map(([label, type]) => (
            <div key={type} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: mediaType === type ? 'rgba(37,211,102,0.18)' : 'rgba(255,255,255,0.05)', color: mediaType === type ? '#25D366' : 'rgba(255,255,255,0.3)', border: `1px solid ${mediaType === type ? 'rgba(37,211,102,0.35)' : 'transparent'}` }}>{label}</div>
          ))}
          {file && <div style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 20, fontSize: 11, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</div>}
        </div>

        {/* ────────────────────────────────────────────────────────────────────────────── */}
        {/* ACTION BUTTONS */}
        {/* ────────────────────────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 11, color: 'rgba(255,255,255,0.55)', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            style={{
              flex: 2,
              padding: 12,
              border: 'none',
              borderRadius: 11,
              fontSize: 14,
              fontWeight: 800,
              cursor: uploading || !file ? 'not-allowed' : 'pointer',
              background: file && !uploading ? 'linear-gradient(135deg, #25D366, #128C7E)' : 'rgba(37,211,102,0.18)',
              color: file && !uploading ? '#fff' : 'rgba(37,211,102,0.55)',
              opacity: uploading ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            {uploading ? '⏳ Uploading…' : file ? '✓ Share Status' : '+ Choose Media'}
          </button>
        </div>
      </div>
    </div>
  );
}








// function StatusUploader({ 
//   onClose, 
//   onUploaded,
//   // ✅ NEW: Accept these props from parent
//   statusMedia = [],
//   setStatusMedia = () => {},
//   mediaValidation = {},
//   setMediaValidation = () => {},
// }) {
//   const [file, setFile]           = useState(null);
//   const [preview, setPreview]     = useState(null);
//   const [mediaType, setMediaType] = useState('image');
//   const [caption, setCaption]     = useState('');
//   const [uploading, setUploading] = useState(false);
//   const [error, setError]         = useState('');
//   const [dragOver, setDragOver]   = useState(false);
//   const fileRef = useRef();

//   const processFile = (f) => {
//     if (!f) return;
//     const isVideo = f.type.startsWith('video/'), isImage = f.type.startsWith('image/');
//     if (!isVideo && !isImage) { setError('Please select an image or video file.'); return; }
//     const maxMB = isVideo ? 50 : 10;
//     if (f.size > maxMB * 1024 * 1024) { setError(`File too large. Max ${maxMB}MB for ${isVideo ? 'videos' : 'images'}.`); return; }
//     setError(''); setFile(f); setMediaType(isVideo ? 'video' : 'image'); setPreview(URL.createObjectURL(f));
//   };

//   const handleUpload = async () => {
//     if (!file) { fileRef.current?.click(); return; }
//     setUploading(true); setError('');
//     try {
//       const fd = new FormData();
//       fd.append('media', file); fd.append('caption', caption.trim());
//       const res = await fetch(`${API_BASE}/statuses/`, { method: 'POST', headers: { Authorization: `Token ${getToken()}` }, body: fd });
//       const data = await res.json();
//       if (!res.ok || !data.success) throw new Error(data.error || 'Upload failed');
//       onUploaded?.(data.status); onClose();
//     } catch (err) { setError(err.message); }
//     finally { setUploading(false); }
//   };

//   return (
//     <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//       <div onClick={e => e.stopPropagation()} style={{ background: '#1a2332', borderRadius: 22, width: 380, maxWidth: '95vw', padding: 28, boxShadow: '0 32px 96px rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.07)' }}>
//         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
//           <div>
//             <h3 style={{ margin: 0, color: '#fff', fontSize: 19, fontWeight: 800 }}>Add Status</h3>
//             <p style={{ margin: '3px 0 0', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Visible for 24 hours</p>
//           </div>
//           <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', color: 'rgba(255,255,255,0.6)', width: 34, height: 34, fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
//         </div>
//         {preview ? (
//           <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#000', height: 220, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//             {mediaType === 'video' ? <video src={preview} controls muted style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }} /> : <img src={preview} alt="Preview" style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }} />}
//             <button onClick={() => { setFile(null); setPreview(null); }} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', color: '#fff', width: 30, height: 30, fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
//             <button onClick={() => fileRef.current?.click()} style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(37,211,102,0.85)', border: 'none', borderRadius: 8, color: '#fff', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Change</button>
//           </div>
//         ) : (
//           <div onClick={() => fileRef.current?.click()} onDrop={e => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files[0]); }} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
//             style={{ height: 190, border: `2px dashed ${dragOver ? '#25D366' : 'rgba(255,255,255,0.13)'}`, borderRadius: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 16, gap: 8, background: dragOver ? 'rgba(37,211,102,0.06)' : 'rgba(255,255,255,0.03)', transition: 'all 0.2s' }}>
//             <div style={{ fontSize: 44, lineHeight: 1 }}>📷</div>
//             <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, fontWeight: 600 }}>Tap to choose photo or video</div>
//             <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>or drag &amp; drop here</div>
//             <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>Images ≤ 10 MB · Videos ≤ 50 MB</div>
//           </div>
//         )}
//         <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />
//         <div style={{ position: 'relative', marginBottom: 14 }}>
//           <input type="text" placeholder="Add a caption… (optional)" value={caption} onChange={e => setCaption(e.target.value.slice(0, 500))}
//             style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 11, padding: '11px 46px 11px 14px', color: '#fff', fontSize: 14, outline: 'none' }} />
//           <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>{caption.length}/500</span>
//         </div>
//         {error && <div style={{ background: 'rgba(239,68,68,0.13)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 9, padding: '9px 13px', color: '#fca5a5', fontSize: 13, marginBottom: 14 }}>{error}</div>}
//         <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
//           {[['🖼 Image','image'],['🎥 Video','video']].map(([label, type]) => (
//             <div key={type} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: mediaType === type ? 'rgba(37,211,102,0.18)' : 'rgba(255,255,255,0.05)', color: mediaType === type ? '#25D366' : 'rgba(255,255,255,0.3)', border: `1px solid ${mediaType === type ? 'rgba(37,211,102,0.35)' : 'transparent'}` }}>{label}</div>
//           ))}
//           {file && <div style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 20, fontSize: 11, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</div>}
//         </div>
//         <div style={{ display: 'flex', gap: 10 }}>
//           <button onClick={onClose} style={{ flex: 1, padding: 12, background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 11, color: 'rgba(255,255,255,0.55)', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
//           <button onClick={handleUpload} disabled={uploading} style={{ flex: 2, padding: 12, border: 'none', borderRadius: 11, fontSize: 14, fontWeight: 800, cursor: uploading ? 'not-allowed' : 'pointer', background: file ? 'linear-gradient(135deg, #25D366, #128C7E)' : 'rgba(37,211,102,0.18)', color: file ? '#fff' : 'rgba(37,211,102,0.55)', opacity: uploading ? 0.7 : 1, transition: 'all 0.2s' }}>
//             {uploading ? '⏳ Uploading…' : file ? '✓ Share Status' : '+ Choose Media'}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS ROW ITEM — WhatsApp style
// ═══════════════════════════════════════════════════════════════════════════════
function StatusRowItem({ group, currentUserEmail, onClick, viewedStatusIds = new Set() }) {
  const isOwn  = (group.user.email    || '').toLowerCase() === currentUserEmail.toLowerCase() ||
               (group.user.username || '').toLowerCase() === currentUserEmail.toLowerCase();
  const hasNew = group.has_unviewed;
  const name   = group.user.username || group.user.email;
  const firstStatus  = group.statuses[0];
  const thumbUrl     = resolveMediaUrl(firstStatus?.thumbnail_url);
  const mediaUrl     = resolveMediaUrl(firstStatus?.media_url);
  const isVideo      = firstStatus?.media_type === 'video' ||
                       /\.(mp4|mov|webm|avi)$/i.test(mediaUrl || '');
  // ✅ Prefer thumbnail_url still. For videos without thumbnail, use video element.
  const stillThumb   = thumbUrl || (!isVideo ? mediaUrl : null);
  const videoFallback = isVideo && !thumbUrl ? mediaUrl : null;
  const displayThumb = stillThumb; // kept for background colour check
  const count  = group.statuses.length;
  const time   = formatTime(group.statuses[0]?.created_at);

  return (
    <div
      onClick={() => onClick(group)}
      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px', cursor: 'pointer', background: '#fff', borderBottom: '1px solid #f0f2f5', transition: 'background 0.12s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f5f6f6'}
      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
    >
      <div style={{ position: 'relative', flexShrink: 0, width: 68, height: 68 }}>
        <div style={{ position: 'absolute', top: 5, left: 5, width: 58, height: 58, borderRadius: '50%', overflow: 'hidden', background: (stillThumb || videoFallback) ? '#111' : avatarColor(name) }}>
          {/* ✅ Still image → <img>. Video with no thumbnail → <video> at first frame. */}
          {stillThumb ? (
            <img
              src={stillThumb}
              alt={name}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : videoFallback ? (
            // ✅ Canvas first-frame capture — no black flash
            <VideoThumb
              src={videoFallback}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }}
              fallback={
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 22, zIndex: 1 }}>
                  {getInitials(name)}
                </div>
              }
            />
          ) : (
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 22, zIndex: 1 }}>
              {getInitials(name)}
            </div>
          )}
        </div>
        <SegmentedRing
          count={count}
          viewed={group.statuses.filter(s => s.is_viewed || viewedStatusIds.has(s.id)).length}
          size={68}
          strokeWidth={3.5}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#111b21', fontWeight: hasNew ? 600 : 400, fontSize: 16, marginBottom: 3, letterSpacing: '-0.1px' }}>
          {isOwn ? 'My Status' : name}
        </div>
        <div style={{ color: '#667781', fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
          {time}
          {isOwn && count > 0 && (
            <span style={{ color: '#8696a0', fontSize: 12 }}>· {count} update{count !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ FIXED STATUS TAB - Counts existing statuses properly
// ═══════════════════════════════════════════════════════════════════════════════
function StatusTab({ user, onBack }) {
  const [groups, setGroups]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);

  

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ MEDIA VALIDATION STATE - Track all uploaded media for limit enforcement
  // ════════════════════════════════════════════════════════════════════════════════
  const [uploadedMediaList, setUploadedMediaList] = useState([]);
  const [mediaValidation, setMediaValidation] = useState({
    videoCount: 0,
    imageCount: 0,
    videosRemaining: 4,
    imagesRemaining: 10,
    error: null,
  });

  // ✅ RING FIX: Load viewed IDs from localStorage so rings are grey on open
  const [viewedIds, setViewedIds] = useState(() => {
    try {
      const raw = localStorage.getItem('viewedStatusIds');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const markViewed = useCallback((statusId) => {
    setViewedIds(prev => {
      const next = new Set(prev);
      next.add(statusId);
      try { localStorage.setItem('viewedStatusIds', JSON.stringify([...next])); } catch {}
      return next;
    });
    setGroups(prev => prev.map(g => ({
      ...g,
      statuses: g.statuses.map(s => s.id === statusId ? { ...s, is_viewed: true } : s),
    })));
  }, []);

  const currentUserEmail = (user?.email || user?.identifier || '').toLowerCase();

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/statuses/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) {
        const data = await res.json();
        const groups = data.groups || [];
        setGroups(groups);
        // ✅ Prune expired status IDs from localStorage (statuses expire after 24h)
        const liveIds = new Set(groups.flatMap(g => g.statuses.map(s => s.id)));
        setViewedIds(prev => {
          const pruned = new Set([...prev].filter(id => liveIds.has(id)));
          try { localStorage.setItem('viewedStatusIds', JSON.stringify([...pruned])); } catch {}
          return pruned;
        });
      }
    } catch (e) { console.error('fetch statuses:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  const isMyGroup = (g) => {
    const gEmail    = (g.user.email    || '').toLowerCase();
    const gUsername = (g.user.username || '').toLowerCase();
    return gEmail === currentUserEmail || gUsername === currentUserEmail;
  };
  const myGroup     = groups.find(isMyGroup);
  const otherGroups = groups.filter(g => !isMyGroup(g));
  const rawThumb  = myGroup?.statuses[0]?.thumbnail_url || myGroup?.statuses[0]?.media_url;
  const myThumb   = resolveMediaUrl(rawThumb);
  const myIsVideo = myGroup?.statuses[0]?.media_type === 'video' ||
                    (myThumb || '').match(/\.(mp4|mov|webm|avi)$/i);






  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ COUNT EXISTING STATUSES FROM myGroup.statuses
  // This runs whenever myGroup changes and calculates remaining slots
  // ════════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!myGroup || !myGroup.statuses) {
      setMediaValidation({
        videoCount: 0,
        imageCount: 0,
        videosRemaining: 4,
        imagesRemaining: 10,
        error: null,
      });
      return;
    }

    // ✅ Count existing videos and images from myGroup.statuses
    const existingVideos = myGroup.statuses.filter(s => 
      s.media_type === 'video' || 
      /\.(mp4|mov|webm|avi)$/i.test((s.media_url || s.thumbnail_url || ''))
    ).length;

    const existingImages = myGroup.statuses.filter(s => 
      s.media_type === 'image' || 
      (!/\.(mp4|mov|webm|avi)$/i.test((s.media_url || s.thumbnail_url || '')) && 
       s.media_type !== 'video')
    ).length;

    // ✅ Calculate remaining slots
    const videosRemaining = Math.max(0, 4 - existingVideos);
    const imagesRemaining = Math.max(0, 10 - existingImages);

    console.log('📊 Status Count:', {
      totalStatuses: myGroup.statuses.length,
      videos: existingVideos,
      images: existingImages,
      videosRemaining,
      imagesRemaining,
    });

    setMediaValidation({
      videoCount: existingVideos,
      imageCount: existingImages,
      videosRemaining,
      imagesRemaining,
      error: null,
    });
  }, [myGroup?.statuses]);
                    

  

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ HANDLE UPLOAD COMPLETION - Reset media list and validation
  // ════════════════════════════════════════════════════════════════════════════════
  const handleUploaderClose = useCallback(() => {
    setShowUploader(false);
    setUploadedMediaList([]);
    // ✅ Don't reset mediaValidation to zeros — let the useEffect on myGroup.statuses
    // recalculate from database data so limits stay accurate
  }, []);

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ HANDLE UPLOAD SUCCESS - Refresh statuses and close uploader
  // ════════════════════════════════════════════════════════════════════════════════
  const handleUploaded = useCallback(() => {
    setShowUploader(false);
    setUploadedMediaList([]);
    // ✅ Refresh from database — useEffect on myGroup.statuses will recalculate counts
    fetchStatuses();
  }, [fetchStatuses]);

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ HANDLE VIEWER CLOSE - Refresh statuses and clear viewing state
  // ════════════════════════════════════════════════════════════════════════════════
  const handleViewerClose = useCallback(() => {
    setViewingGroup(null);
    fetchStatuses();
  }, [fetchStatuses]);

  // ══════════════════════════════════════════════════════════��═════════════════════
  // ✅ HANDLE VIEWER DELETED - Refresh statuses when user deletes their status
  // ════════════════════════════════════════════════════════════════════════════════
  const handleStatusDeleted = useCallback(() => {
    fetchStatuses();
  }, [fetchStatuses]);






  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid #f0f2f5', background: '#fff', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#54656f', fontSize: 22, cursor: 'pointer', padding: '0 2px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 style={{ margin: 0, color: '#111b21', fontSize: 20, fontWeight: 700, flex: 1, letterSpacing: '-0.3px' }}>Status</h2>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
        <div
          onClick={() => { if (myGroup) setViewingGroup(myGroup); }}
          style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', cursor: myGroup ? 'pointer' : 'default', borderBottom: '1px solid #f0f2f5', background: '#fff', transition: 'background 0.12s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#f5f6f6'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <div style={{ position: 'relative', flexShrink: 0, width: 68, height: 68 }}>
            <div style={{ position: 'absolute', top: 5, left: 5, width: 58, height: 58, borderRadius: '50%', overflow: 'hidden', background: avatarColor(user?.username || 'U') }}>
              {!myThumb && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 24, zIndex: 1 }}>
                  {getInitials(user?.username || user?.identifier || 'U')}
                </div>
              )}
              {myThumb && (
                myIsVideo
                  ? <VideoThumb src={myThumb} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }} />
                  : <img src={myThumb} alt="" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }} onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
            <SegmentedRing count={myGroup ? myGroup.statuses.length : 0} viewed={0} size={68} strokeWidth={3.5} />
            <div
              onPointerDown={e => { e.stopPropagation(); e.preventDefault(); setShowUploader(true); }}
              title="Add a new status update"
              style={{ position: 'absolute', bottom: -2, right: -2, width: 30, height: 30, borderRadius: '50%', background: '#25D366', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.25)', zIndex: 10, transition: 'transform 0.15s, background 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; e.currentTarget.style.background = '#1fbc5b'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#25D366'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><path d="M12 5v14M5 12h14"/></svg>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ color: '#111b21', fontWeight: 600, fontSize: 16, marginBottom: 3, letterSpacing: '-0.1px' }}>
              {myGroup ? 'My Status' : 'Add status'}
            </div>
            <div style={{ color: '#667781', fontSize: 13.5 }}>
              {myGroup
                ? `${myGroup.statuses.length} update${myGroup.statuses.length !== 1 ? 's' : ''} · Disappears after 24 hours`
                : 'Disappears after 24 hours'}
            </div>
          </div>

          <button
            onPointerDown={e => { e.stopPropagation(); e.preventDefault(); setShowUploader(true); }}
            title="Add a new status update"
            style={{ background: 'rgba(37,211,102,0.1)', border: '1.5px solid rgba(37,211,102,0.4)', borderRadius: 20, padding: '6px 14px', color: '#128C7E', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.1)'; }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 5v14M5 12h14"/></svg>
            Add
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#8696a0', fontSize: 14 }}>Loading statuses…</div>
        ) : otherGroups.length === 0 ? (
          <div style={{ padding: '70px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>👁</div>
            <div style={{ color: '#111b21', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No status updates yet</div>
            <div style={{ color: '#8696a0', fontSize: 13.5, lineHeight: 1.5 }}>When others post statuses, they'll appear here</div>
          </div>
        ) : (
          <>
            <div style={{ padding: '16px 20px 8px', color: '#8696a0', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px', background: '#f8fafc', borderBottom: '1px solid #f0f2f5' }}>
              Recent updates
            </div>
            {otherGroups.map(g => (
              <StatusRowItem
                key={g.user.id}
                group={g}
                currentUserEmail={currentUserEmail}
                onClick={setViewingGroup}
                viewedStatusIds={viewedIds}
              />
            ))}
          </>
        )}
      </div>



      {/* ════════════════════════════════════════════════════════════════════════════════ */}
      {/* ✅ UPDATED: StatusUploader with media validation props */}
      {/* ════════════════════════════════════════════════════════════════════════════════ */}
      {showUploader && (
        <StatusUploader
          onClose={handleUploaderClose}
          onUploaded={handleUploaded}
          statusMedia={uploadedMediaList}
          setStatusMedia={setUploadedMediaList}
          mediaValidation={mediaValidation}
          setMediaValidation={setMediaValidation}
        />
      )}

      {/* ════════════════════════════════════════════════════════════════════════════════ */}
      {/* ✅ UPDATED: StatusViewer with proper close and delete handlers */}
      {/* ════════════════════════════════════════════════════════════════════════════════ */}
      {viewingGroup && (
        <StatusViewer
          group={viewingGroup}
          currentUserEmail={currentUserEmail}
          onClose={handleViewerClose}
          onDeleted={handleStatusDeleted}
          onViewed={markViewed}
        />
      )}
    </div>
  );
}

      {/* {showUploader && <StatusUploader onClose={() => setShowUploader(false)} onUploaded={() => { setShowUploader(false); fetchStatuses(); }} />}
      {viewingGroup && <StatusViewer group={viewingGroup} currentUserEmail={currentUserEmail}
        onClose={() => { setViewingGroup(null); fetchStatuses(); }}
        onDeleted={fetchStatuses}
        onViewed={markViewed} />}
    </div>
  );
} */}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVERSATION ROW
// ═══════════════════════════════════════════════════════════════════════════════
function ConversationRow({ groupedConv, currentUserEmail, onClick, isUnread }) {
  const conversation    = groupedConv.conversations[0];
  const isDirectMessage = conversation.type === 'direct_message';
  const buyerEmail      = (conversation.buyer?.email || '').toLowerCase();
  const isBuyer         = buyerEmail === currentUserEmail.toLowerCase();
  const otherParty      = isBuyer ? conversation.seller : conversation.buyer;
  const otherName       = otherParty?.username || otherParty?.email || 'Unknown User';
  const lastMsg         = conversation.last_message || conversation.message ||
    (isDirectMessage ? 'Message about product' : `Offer: KES ${Number(conversation.amount || 0).toLocaleString()}`);
  const time            = formatTime(conversation.updated_at || conversation.created_at);
  const unreadCount     = groupedConv.totalUnread;
  const convCount       = groupedConv.conversations.length;
  const isFlagged       = conversation.is_flagged || conversation.status === 'flagged';

  return (
    <div className={styles.chatRow} onClick={() => onClick(groupedConv)}>
      <div className={styles.chatRowAvatar}>
        <Avatar name={otherName} />
        <span className={styles.onlineDot} />
      </div>
      <div className={styles.chatRowBody}>
        <div className={styles.chatRowTop}>
          <span className={`${styles.chatRowName} ${isUnread ? styles.chatRowNameBold : ''}`}>
            {otherName}
            {convCount > 1 && <span className={styles.conversationCount}> ({convCount})</span>}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {isFlagged && <span style={{ fontSize: 12 }}>🚩</span>}
            <span className={`${styles.chatRowTime} ${isUnread ? styles.chatRowTimeUnread : ''}`}>{time}</span>
          </div>
        </div>
        <div className={styles.chatRowBottom}>
          <span className={`${styles.chatRowPreview} ${isUnread ? styles.chatRowPreviewBold : ''}`}>
            {isFlagged
              ? <span style={{ color: '#f87171', fontSize: 11 }}>⚠ Flagged by admin · messaging disabled</span>
              : <><span className={styles.checkmarks}>✓✓ </span>{(() => { const clean = stripStatusReply(lastMsg); return clean.length > 38 ? clean.slice(0, 38) + '…' : clean; })()}</>
            }
          </span>
          {!isFlagged && unreadCount > 0
            ? <span className={styles.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
            : !isFlagged && !isDirectMessage && (
              <span className={styles.statusPill} style={{ background: getStatusColor(conversation.status) + '22', color: getStatusColor(conversation.status) }}>
                {getStatusLabel(conversation.status)}
              </span>
            )
          }
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVERSATION LIST MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function ConversationListModal({ groupedConv, currentUserEmail, onSelectConversation, onClose }) {
  const conversation = groupedConv.conversations[0];
  const buyerEmail   = (conversation.buyer?.email || '').toLowerCase();
  const isBuyer      = buyerEmail === currentUserEmail.toLowerCase();
  const otherParty   = isBuyer ? conversation.seller : conversation.buyer;
  const otherName    = otherParty?.username || otherParty?.email || 'Unknown User';

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.conversationListModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <button className={styles.backBtn} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div className={styles.modalHeaderInfo}>
            <Avatar name={otherName} size={38} />
            <div>
              <div className={styles.modalHeaderName}>{otherName}</div>
              <div className={styles.modalHeaderSub}>{groupedConv.conversations.length} conversation{groupedConv.conversations.length > 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>
        <div className={styles.conversationList}>
          {groupedConv.conversations.map(conv => {
            const isDirectMsg = conv.type === 'direct_message';
            const bEmail      = (conv.buyer?.email || '').toLowerCase();
            const isBuy       = bEmail === currentUserEmail.toLowerCase();
            const unread      = isBuy ? (conv.unread_by_buyer || 0) : (conv.unread_by_seller || 0);
            const flagged     = conv.is_flagged || conv.status === 'flagged';
            return (
              <div key={conv.id} className={styles.conversationItem} onClick={() => onSelectConversation(conv)}>
                <div className={styles.conversationItemIcon}>{flagged ? '🚩' : isDirectMsg ? '💬' : '💰'}</div>
                <div className={styles.conversationItemBody}>
                  <div className={styles.conversationItemTitle}>{conv.ad?.title || 'Product conversation'}</div>
                  <div className={styles.conversationItemMeta}>
                    {flagged
                      ? <span style={{ color: '#f87171', fontSize: 11 }}>Flagged by admin</span>
                      : isDirectMsg
                        ? <span className={styles.conversationItemType}>Direct Message</span>
                        : <>
                            <span className={styles.conversationItemType}>Offer</span>
                            <span className={styles.conversationItemDivider}>•</span>
                            <span className={styles.conversationItemAmount}>KES {Number(conv.amount || 0).toLocaleString()}</span>
                            <span className={styles.conversationItemDivider}>•</span>
                            <span className={styles.conversationItemStatus} style={{ color: getStatusColor(conv.status) }}>{getStatusLabel(conv.status)}</span>
                          </>
                    }
                  </div>
                  <div className={styles.conversationItemTime}>{formatTime(conv.updated_at || conv.created_at)}</div>
                </div>
                {unread > 0 && <span className={styles.conversationItemBadge}>{unread}</span>}
                <svg className={styles.conversationItemArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MESSAGE BUBBLE — renders plain text OR status reply snippet (WhatsApp style)
// Detects __SREPLY__{json}__\nUserText format and shows a visual quoted card.
// ═══════════════════════════════════════════════════════════════════════════════
function MessageBubble({ message, isMe, time, styles }) {
  const SREPLY_RE = /^__SREPLY__(\{.*?\})__\n?([\s\S]*)$/;
  const match = message.match(SREPLY_RE);

  if (!match) {
    // Plain message — render normally
    return (
      <div className={`${styles.bubble} ${isMe ? styles.bubbleMe : styles.bubbleThem}`}>
        <div className={styles.bubbleText}>{message}</div>
        <div className={styles.bubbleMeta}>
          <span className={styles.bubbleTime}>{time}</span>
          {isMe && <span className={styles.bubbleTick}>✓✓</span>}
        </div>
      </div>
    );
  }

  // Status reply message — parse metadata + render snippet card
  let meta = {};
  try { meta = JSON.parse(match[1]); } catch { meta = {}; }
  const userText   = (match[2] || '').trim();
  const isVideo    = meta.type === 'video';
  const thumbSrc   = meta.thumb    || '';
  const videoSrc   = meta.videoSrc || '';  // fallback for videos without thumbnail
  const caption    = meta.caption  || '';
  const posterName = meta.poster   || '';
  // hasMedia: true when we have either a still thumbnail or a video src
  const hasThumb   = !!(thumbSrc || videoSrc);

  return (
    <div className={`${styles.bubble} ${isMe ? styles.bubbleMe : styles.bubbleThem}`}
      style={{ padding: '4px 4px 6px', minWidth: 180, maxWidth: '72%' }}>

      {/* ── Quoted status snippet card ── */}
      <div style={{
        display: 'flex', alignItems: 'stretch',
        background: isMe ? 'rgba(0,0,0,0.10)' : 'rgba(0,0,0,0.06)',
        borderRadius: '8px 8px 0 0',
        borderLeft: `4px solid ${isMe ? '#25D366' : '#6366f1'}`,
        overflow: 'hidden', marginBottom: 4,
        minHeight: 54,
      }}>
        {/* Text side */}
        <div style={{ flex: 1, padding: '7px 10px', minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 700,
            color: isMe ? '#25D366' : '#6366f1',
            marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {isVideo ? '🎥' : '🖼'} {posterName ? `${posterName} • Status` : 'Status'}
          </div>
          {caption ? (
            <div style={{
              fontSize: 12, color: '#374151', lineHeight: 1.35,
              overflow: 'hidden', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>{caption}</div>
          ) : (
            <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
              {isVideo ? 'Video' : 'Photo'}
            </div>
          )}
        </div>

        {/* Thumbnail side — still image preferred, video element fallback */}
        <div style={{
          width: 62, flexShrink: 0, position: 'relative',
          background: '#1a1a1a', overflow: 'hidden',
        }}>
          {thumbSrc ? (
            /* Still thumbnail (image or pre-generated video frame) */
            <img
              src={thumbSrc}
              alt="status"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : videoSrc ? (
            /* Video without thumbnail — render muted video; browser shows first frame */
            <video
              src={videoSrc}
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            /* No media at all — show emoji placeholder */
            <div style={{
              width: '100%', height: '100%', minHeight: 54,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, background: isVideo ? '#1a1a1a' : '#e5e7eb',
            }}>
              {isVideo ? '🎥' : '🖼'}
            </div>
          )}
          {/* Play overlay for videos */}
          {isVideo && (thumbSrc || videoSrc) && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.30)',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* ── User's reply text ── */}
      {userText && (
        <div className={styles.bubbleText} style={{ padding: '2px 8px 0' }}>
          {userText}
        </div>
      )}

      <div className={styles.bubbleMeta} style={{ paddingRight: 8 }}>
        <span className={styles.bubbleTime}>{time}</span>
        {isMe && <span className={styles.bubbleTick}>✓✓</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DIRECT MESSAGE WINDOW
// ═══════════════════════════════════════════════════════════════════════════════
function DirectMessageWindow({ conversation, currentUser, onClose, onRefresh }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const chatEndRef = useRef(null);

  const currentUserEmail    = (currentUser?.email || currentUser?.identifier || '').toLowerCase();
  const buyerEmail          = (conversation.buyer?.email || '').toLowerCase();
  const isBuyer             = buyerEmail === currentUserEmail;
  const otherParty          = isBuyer ? conversation.seller : conversation.buyer;
  const otherName           = otherParty?.username || otherParty?.email || 'Unknown User';
  const conversationContext = { otherEmail: (otherParty?.email || '').toLowerCase(), otherUsername: (otherParty?.username || '').toLowerCase(), otherName };
  const isFlagged = conversation.is_flagged || conversation.status === 'flagged';

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/ads/${conversation.ad_id}/messages/thread/${conversation.other_user_id}/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) { const data = await res.json(); setMessages(data.messages || []); }
    } catch (e) { console.error('fetch messages:', e); }
    finally { setLoading(false); }
  }, [conversation.ad_id, conversation.other_user_id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || isFlagged) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/ads/${conversation.ad_id}/messages/`, {
        method: 'POST', headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) { setInput(''); fetchMessages(); onRefresh?.(); }
    } finally { setSending(false); }
  };

  const grouped = messages.reduce((g, m) => { const d = formatChatDate(m.created_at); if (!g[d]) g[d] = []; g[d].push(m); return g; }, {});

  return (
    <div className={styles.chatWindow}>
      <div className={styles.chatWindowHeader}>
        <button className={styles.backBtn} onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div className={styles.chatWindowAvatar}><Avatar name={otherName} size={38} /><span className={styles.onlineDot} style={{ bottom: 0, right: 0 }} /></div>
        <div className={styles.chatWindowInfo}>
          <div className={styles.chatWindowName}>{otherName}{isFlagged && <span style={{ marginLeft: 8, fontSize: 11, color: '#f87171', fontWeight: 700, background: 'rgba(239,68,68,0.1)', padding: '2px 7px', borderRadius: 10 }}>🚩 Flagged</span>}</div>
          <div className={styles.chatWindowSub}>Re: {conversation.ad?.title}</div>
        </div>
      </div>
      <div className={styles.chatMessages}>
        {loading ? <div className={styles.chatLoading}>Loading messages…</div>
          : messages.length === 0 ? <div className={styles.chatEmpty}><div className={styles.chatEmptyIcon}>💬</div><p>No messages yet.<br />Start the conversation!</p></div>
          : Object.entries(grouped).map(([date, msgs]) => (
            <div key={date}>
              <div className={styles.dateSeparator}><span className={styles.dateBadge}>{date}</span></div>
              {msgs.map(msg => {
                const isMe = isMessageFromCurrentUser(msg, currentUserEmail, conversationContext);
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg.message}
                    isMe={isMe}
                    time={formatChatTime(msg.created_at)}
                    styles={styles}
                  />
                );
              })}
            </div>
          ))
        }
        <div ref={chatEndRef} />
      </div>
      {isFlagged ? <FlaggedBanner /> : (
        <div className={styles.chatInputRow}>
          <div className={styles.chatInputWrap}>
            <input className={styles.chatInput} placeholder="Type a message" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} autoFocus />
          </div>
          <button className={`${styles.sendBtn} ${input.trim() ? styles.sendBtnActive : ''}`} onClick={handleSend} disabled={!input.trim() || sending}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// OFFER CHAT WINDOW
// ═══════════════════════════════════════════════════════════════════════════════
function OfferChatWindow({ offer, currentUser, onClose, onRefresh }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);
  const [responding, setResponding] = useState(false);
  const chatEndRef = useRef(null);

  const currentUserEmail    = (currentUser?.email || currentUser?.identifier || '').toLowerCase();
  const buyerEmail          = (offer.buyer?.email || '').toLowerCase();
  const isBuyer             = buyerEmail === currentUserEmail;
  const otherParty          = isBuyer ? offer.seller : offer.buyer;
  const otherName           = otherParty?.username || otherParty?.email || 'Unknown User';
  const conversationContext = { otherEmail: (otherParty?.email || '').toLowerCase(), otherUsername: (otherParty?.username || '').toLowerCase(), otherName };
  const isFlagged = offer.is_flagged || offer.status === 'flagged';

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/offers/${offer.id}/messages/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) setMessages(await res.json());
    } catch (e) { console.error('fetch messages:', e); }
    finally { setLoading(false); }
  }, [offer.id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || isFlagged) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/offers/${offer.id}/messages/send/`, {
        method: 'POST', headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) { setInput(''); fetchMessages(); onRefresh?.(); }
    } finally { setSending(false); }
  };

  const handleRespond = async (action) => {
    setResponding(true);
    try {
      const res = await fetch(`${API_BASE}/offers/${offer.id}/respond/`, {
        method: 'POST', headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) { fetchMessages(); onRefresh?.(); }
    } finally { setResponding(false); }
  };

  const grouped = messages.reduce((g, m) => { const d = formatChatDate(m.created_at); if (!g[d]) g[d] = []; g[d].push(m); return g; }, {});

  return (
    <div className={styles.chatWindow}>
      <div className={styles.chatWindowHeader}>
        <button className={styles.backBtn} onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div className={styles.chatWindowAvatar}><Avatar name={otherName} size={38} /><span className={styles.onlineDot} style={{ bottom: 0, right: 0 }} /></div>
        <div className={styles.chatWindowInfo}>
          <div className={styles.chatWindowName}>{otherName}{isFlagged && <span style={{ marginLeft: 8, fontSize: 11, color: '#f87171', fontWeight: 700, background: 'rgba(239,68,68,0.1)', padding: '2px 7px', borderRadius: 10 }}>🚩 Flagged</span>}</div>
          <div className={styles.chatWindowSub}>{offer.ad?.title ? `Re: ${offer.ad.title}` : 'Offer conversation'}</div>
        </div>
      </div>
      <div className={styles.offerPill}>
        <span className={styles.offerPillAmount}>KES {Number(offer.amount || 0).toLocaleString()}</span>
        <span className={styles.offerPillDivider}>·</span>
        <span className={styles.offerPillLabel}>{offer.ad?.title || 'Product'}</span>
        <span className={styles.offerPillStatus} style={{ color: getStatusColor(offer.status) }}>{getStatusLabel(offer.status)}</span>
      </div>
      {!isBuyer && offer.status === 'pending' && !isFlagged && (
        <div className={styles.respondBar}>
          <button className={`${styles.respondBtn} ${styles.respondAccept}`}  onClick={() => handleRespond('accept')}  disabled={responding}>✓ Accept</button>
          <button className={`${styles.respondBtn} ${styles.respondCounter}`} onClick={() => handleRespond('counter')} disabled={responding}>↩ Counter</button>
          <button className={`${styles.respondBtn} ${styles.respondReject}`}  onClick={() => handleRespond('reject')}  disabled={responding}>✕ Decline</button>
        </div>
      )}
      <div className={styles.chatMessages}>
        {loading ? <div className={styles.chatLoading}>Loading messages…</div>
          : messages.length === 0 ? <div className={styles.chatEmpty}><div className={styles.chatEmptyIcon}>💬</div><p>No messages yet.<br />Start the conversation!</p></div>
          : Object.entries(grouped).map(([date, msgs]) => (
            <div key={date}>
              <div className={styles.dateSeparator}><span className={styles.dateBadge}>{date}</span></div>
              {msgs.map(msg => {
                const isMe = isMessageFromCurrentUser(msg, currentUserEmail, conversationContext);
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg.message}
                    isMe={isMe}
                    time={formatChatTime(msg.created_at)}
                    styles={styles}
                  />
                );
              })}
            </div>
          ))
        }
        <div ref={chatEndRef} />
      </div>
      {isFlagged ? <FlaggedBanner /> : (
        <div className={styles.chatInputRow}>
          <div className={styles.chatInputWrap}>
            <input className={styles.chatInput} placeholder="Type a message" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} autoFocus />
          </div>
          <button className={`${styles.sendBtn} ${input.trim() ? styles.sendBtnActive : ''}`} onClick={handleSend} disabled={!input.trim() || sending}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNT SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
function AccountSettings({ user }) {
  return (
    <div className={styles.settingsContainer}>
      <h2 className={styles.settingsTitle}>Account Settings</h2>
      <div className={styles.settingsGrid}>
        {[
          { icon: '🔔', title: 'Notifications', desc: 'Manage email and push notifications',    btn: 'Configure', grad: 'linear-gradient(135deg,#667eea,#764ba2)' },
          { icon: '🔒', title: 'Privacy',        desc: 'Control who can see your information',  btn: 'Manage',    grad: 'linear-gradient(135deg,#f093fb,#f5576c)' },
          { icon: '🔐', title: 'Security',        desc: 'Change password and security settings', btn: 'Update',    grad: 'linear-gradient(135deg,#4facfe,#00f2fe)' },
          { icon: '⚙️', title: 'Preferences',    desc: 'Customize your experience',             btn: 'Customize', grad: 'linear-gradient(135deg,#fa709a,#fee140)' },
        ].map(s => (
          <div key={s.title} className={styles.settingCard}>
            <div className={styles.cardIcon} style={{ background: s.grad }}>{s.icon}</div>
            <div className={styles.cardContent}><h3 className={styles.cardTitle}>{s.title}</h3><p className={styles.cardDescription}>{s.desc}</p></div>
            <button className={styles.cardButton}>{s.btn} <svg className={styles.buttonArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></button>
          </div>
        ))}
      </div>
      <div className={styles.quickActions}>
        <h3 className={styles.quickActionsTitle}>Quick Actions</h3>
        <div className={styles.quickActionsList}>
          {[['📧','Change Email'],['📱','Update Phone'],['🔑','Reset Password'],['🗑️','Delete Account']].map(([icon, label]) => (
            <button key={label} className={styles.quickActionBtn}><span className={styles.quickActionIcon}>{icon}</span><span>{label}</span></button>
          ))}
        </div>
      </div>
    </div>
  );
}

function groupConversationsByUser(conversations, currentUserEmail) {
  const grouped = {};
  conversations.forEach(conv => {
    const buyerEmail = (conv.buyer?.email || '').toLowerCase();
    const isBuyer    = buyerEmail === currentUserEmail.toLowerCase();
    const otherUser  = isBuyer ? conv.seller : conv.buyer;
    const otherEmail = (otherUser?.email || '').toLowerCase();
    if (!otherEmail) return;
    if (!grouped[otherEmail]) grouped[otherEmail] = { otherUser, otherEmail, conversations: [], totalUnread: 0, mostRecentTime: null };
    grouped[otherEmail].conversations.push(conv);
    const unread = isBuyer ? (conv.unread_by_buyer || 0) : (conv.unread_by_seller || 0);
    grouped[otherEmail].totalUnread += unread;
    const t = new Date(conv.updated_at || conv.created_at);
    if (!grouped[otherEmail].mostRecentTime || t > grouped[otherEmail].mostRecentTime) grouped[otherEmail].mostRecentTime = t;
  });
  return Object.values(grouped).sort((a, b) => b.mostRecentTime - a.mostRecentTime);
}

// ═══════════════════════════════════════════════════════════════════════════════
// I'M SELLING PANEL — system messages / announcements channel
// ═══════════════════════════════════════════════════════════════════════════════
function IMSellingPanel({ onBack, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null); // opened message
  const [unread, setUnread]     = useState(0);

  // Fetch system messages on mount and immediately mark all as read
  // so the badge clears as soon as the user opens the panel.
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/system-messages/`, {
          headers: { Authorization: `Token ${getToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
          const unreadCount = data.unread_count || 0;
          setUnread(unreadCount);

          // ✅ FIX: Auto-mark all as read immediately when panel opens.
          // This clears the badge on the parent the moment the user sees the panel.
          if (unreadCount > 0) {
            try {
              await fetch(`${API_BASE}/auth/system-messages/read-all/`, {
                method: 'POST',
                headers: { Authorization: `Token ${getToken()}` },
              });
              setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
              setUnread(0);
              onUnreadChange?.(0); // ✅ tell parent badge is now 0
            } catch (_) {
              onUnreadChange?.(unreadCount);
            }
          } else {
            onUnreadChange?.(0);
          }
        }
      } catch (e) {
        console.error('fetch system messages:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // Mark a single message as read when opened
  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.is_read) {
      try {
        await fetch(`${API_BASE}/auth/system-messages/${msg.id}/read/`, {
          method: 'POST',
          headers: { Authorization: `Token ${getToken()}` },
        });
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        const newUnread = Math.max(0, unread - 1);
        setUnread(newUnread);
        onUnreadChange?.(newUnread); // ✅ keep parent in sync
      } catch (_) {}
    }
  };

  // Mark all messages as read
  const markAllRead = async () => {
    try {
      await fetch(`${API_BASE}/auth/system-messages/read-all/`, {
        method: 'POST',
        headers: { Authorization: `Token ${getToken()}` },
      });
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnread(0);
      onUnreadChange?.(0); // ✅ keep parent in sync
    } catch (_) {}
  };

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', background: '#f5f6f6', minHeight: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e9edef', flexShrink: 0 }}>
          <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🏪</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111b21' }}>I&apos;m Selling</div>
            <div style={{ fontSize: 12, color: '#667781' }}>Official message</div>
          </div>
        </div>

        {/* Message bubble */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
          {/* Date stamp */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{ background: '#e9edef', color: '#54656f', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>
              {formatChatDate(selected.created_at)}
            </span>
          </div>

          {/* Chat bubble */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div style={{ background: '#fff', borderRadius: '0 12px 12px 12px', padding: '12px 16px', maxWidth: '85%', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111b21', marginBottom: 8 }}>
                {selected.title}
              </div>
              <div style={{ fontSize: 13.5, color: '#3b4a54', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {selected.body}
              </div>
              <div style={{ textAlign: 'right', marginTop: 8, fontSize: 11, color: '#8696a0' }}>
                {formatChatTime(selected.created_at)} ✓✓
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#f5f6f6', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e9edef', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🏪</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111b21' }}>I&apos;m Selling</div>
          <div style={{ fontSize: 12, color: '#667781' }}>
            {loading ? 'Loading…' : `${messages.length} message${messages.length !== 1 ? 's' : ''}${unread > 0 ? ` · ${unread} unread` : ''}`}
          </div>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6366f1', fontWeight: 600, padding: '4px 8px' }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Message list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#667781', fontSize: 13 }}>
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '70px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🏪</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#54656f' }}>No messages yet</div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#8696a0', lineHeight: 1.5 }}>
              Announcements and updates from I&apos;m Selling will appear here.
            </div>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              onClick={() => openMessage(msg)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '14px 16px',
                background: msg.is_read ? '#fff' : '#f0f4ff',
                borderBottom: '1px solid #f0f2f5',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f5f6f6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = msg.is_read ? '#fff' : '#f0f4ff'; }}
            >
              <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏪</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontWeight: msg.is_read ? 600 : 800, fontSize: 14, color: '#111b21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {msg.title}
                  </span>
                  <span style={{ fontSize: 11, color: '#8696a0', flexShrink: 0, marginLeft: 8 }}>
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#667781', display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                  {!msg.is_read && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', flexShrink: 0, display: 'inline-block' }} />
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {msg.body.split('\n')[0]}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// LIKES PANEL
// ═══════════════════════════════════════════════════════════════════════════════
function LikesPanel({ myLikes, onBack, onMarkSeen }) {
  useEffect(() => { onMarkSeen(); }, [onMarkSeen]);
  return (
    <div className={styles.container} style={{ background: '#f5f6f6', minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e9edef', flexShrink: 0 }}>
        <button onClick={onBack} className={styles.backBtn}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg></button>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#f43f5e,#fb923c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>❤️</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111b21' }}>Likes</div>
          <div style={{ fontSize: 12, color: '#667781' }}>{myLikes.length === 0 ? 'No one has liked your listings yet' : `${myLikes.length} like${myLikes.length !== 1 ? 's' : ''} on your listings`}</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: '#f5f6f6' }}>
        {myLikes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '70px 24px' }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>❤️</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#54656f' }}>No likes yet</div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#8696a0', lineHeight: 1.5 }}>When someone likes one of your listings, you'll see it here.</div>
          </div>
        ) : myLikes.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f2f5' }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: avatarColor(item.liker_username || 'U'), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 }}>{getInitials(maskLikerEmail(item.liker_username || item.liker_email))}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#111b21', fontWeight: 600, fontSize: 14 }}>{maskLikerEmail(item.liker_username || item.liker_email)}</div>
              <div style={{ color: '#667781', fontSize: 12.5, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                <span style={{ fontSize: 13 }}>❤️</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>liked <span style={{ color: '#374151', fontWeight: 500 }}>{item.ad_title}</span></span>
              </div>
              {item.ad_price && <div style={{ color: '#25D366', fontWeight: 700, fontSize: 12, marginTop: 2 }}>KES {Number(item.ad_price).toLocaleString()}{item.ad_city && <span style={{ color: '#8696a0', fontWeight: 400, marginLeft: 6 }}>· 📍 {item.ad_city}</span>}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              <div style={{ width: 46, height: 46, borderRadius: 8, overflow: 'hidden', background: '#e9edef' }}>
                {item.ad_image ? <img src={item.ad_image} alt={item.ad_title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🛍️</div>}
              </div>
              <span style={{ color: '#8696a0', fontSize: 11 }}>{formatTime(item.liked_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOWERS PANEL
// ═══════════════════════════════════════════════════════════════════════════════
function FollowersPanel({ myFollowers, onBack, onMarkSeen }) {
  useEffect(() => { onMarkSeen(); }, [onMarkSeen]);
  return (
    <div className={styles.container} style={{ background: '#f5f6f6', minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e9edef', flexShrink: 0 }}>
        <button onClick={onBack} className={styles.backBtn}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg></button>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👥</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111b21' }}>Followers</div>
          <div style={{ fontSize: 12, color: '#667781' }}>{myFollowers.length === 0 ? 'No followers yet' : `${myFollowers.length} follower${myFollowers.length !== 1 ? 's' : ''}`}</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: '#f5f6f6' }}>
        {myFollowers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '70px 24px', color: '#667781' }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>👥</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#54656f' }}>No followers yet</div>
            <div style={{ fontSize: 13, marginTop: 6, color: '#8696a0', lineHeight: 1.5 }}>When someone follows you, they'll appear here.</div>
          </div>
        ) : myFollowers.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 18px', background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f0f2f5' }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: avatarColor(item.follower_username || 'U'), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 }}>{getInitials(item.follower_username || item.follower_email || 'U')}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#111b21', fontWeight: 600, fontSize: 14.5 }}>{item.follower_username || item.follower_email || 'Someone'}</div>
              <div style={{ color: '#667781', fontSize: 13, marginTop: 2 }}>👥 started following you</div>
            </div>
            <div style={{ color: '#8696a0', fontSize: 11, flexShrink: 0 }}>{formatTime(item.followed_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN MESSAGES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const MessagesTab = ({ user, messages, setMessages, onInboxRefresh }) => {
  const [activeTab, setActiveTab]               = useState('chats');
  const [conversations, setConversations]       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [search, setSearch]                     = useState('');
  const [openConversation, setOpenConversation] = useState(null);
  const [showConversationList, setShowConversationList] = useState(null);
  const [statusGroups, setStatusGroups]         = useState([]);
  const [showUploader, setShowUploader]         = useState(false);
  const [showingStatus, setShowingStatus]       = useState(null);
  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ MEDIA VALIDATION STATES - Lines ~2680-2690
  // These states track media limits for status uploads (max 4 videos, 10 images)
  // ════════════════════════════════════════════════════════════════════════════════
  const [statusMedia, setStatusMedia] = useState([]);
  const [mediaValidation, setMediaValidation] = useState({
    videoCount: 0,
    imageCount: 0,
    videosRemaining: 4,
    imagesRemaining: 10,
    error: null,
  });




  // ✅ RING FIX: Persist viewed status IDs in localStorage so the ring turns grey
  // immediately on load — even for statuses viewed in a previous session.
  // Backend is_viewed is the source of truth, but this is a fast local cache.
  const [viewedStatusIds, setViewedStatusIds] = useState(() => {
    try {
      const raw = localStorage.getItem('viewedStatusIds');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const markStatusViewed = useCallback((statusId) => {
    setViewedStatusIds(prev => {
      const next = new Set(prev);
      next.add(statusId);
      try { localStorage.setItem('viewedStatusIds', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  // ✅ BUG 3 FIX: Immediately update statusGroups when a status is viewed.
  // Also persists to localStorage so ring stays grey on next page load.
  const handleStatusViewed = useCallback((statusId) => {
    markStatusViewed(statusId);
    setStatusGroups(prev => prev.map(g => ({
      ...g,
      statuses: g.statuses.map(s =>
        s.id === statusId ? { ...s, is_viewed: true } : s
      ),
    })));
  }, [markStatusViewed]);
  const [myLikes,     setMyLikes]     = useState([]);
  const [myFollowers, setMyFollowers] = useState([]);
  const [channelView, setChannelView] = useState(null);
  const [seenLikesCount, setSeenLikesCount] = useState(
    () => parseInt(localStorage.getItem('seenLikesCount') || '0', 10)
  );
  const [seenFollowersCount, setSeenFollowersCount] = useState(
    () => parseInt(localStorage.getItem('seenFollowersCount') || '0', 10)
  );
  // ✅ I'm Selling channel unread badge count
  const [imSellingUnread, setImSellingUnread] = useState(0);

  const currentUserEmail = (user?.email || user?.identifier || '').toLowerCase();

  // ✅ FIX: Compute mediaValidation from statusGroups (API data) — same logic as StatusTab
  useEffect(() => {
    const myStatusGroup = statusGroups.find(g => (g.user.email || '').toLowerCase() === currentUserEmail || (g.user.username || '').toLowerCase() === currentUserEmail);
    if (!myStatusGroup || !myStatusGroup.statuses) {
      setMediaValidation(prev => prev.error ? prev : {
        videoCount: 0,
        imageCount: 0,
        videosRemaining: 4,
        imagesRemaining: 10,
        error: null,
      });
      return;
    }

    const existingVideos = myStatusGroup.statuses.filter(s =>
      s.media_type === 'video' ||
      /\.(mp4|mov|webm|avi)$/i.test((s.media_url || s.thumbnail_url || ''))
    ).length;

    const existingImages = myStatusGroup.statuses.filter(s =>
      s.media_type === 'image' ||
      (!/\.(mp4|mov|webm|avi)$/i.test((s.media_url || s.thumbnail_url || '')) &&
       s.media_type !== 'video')
    ).length;

    const videosRemaining = Math.max(0, 4 - existingVideos);
    const imagesRemaining = Math.max(0, 10 - existingImages);

    setMediaValidation(prev => ({
      ...prev,
      videoCount: existingVideos,
      imageCount: existingImages,
      videosRemaining,
      imagesRemaining,
    }));
  }, [statusGroups, currentUserEmail]);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/messages/conversations/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) { const data = await res.json(); if (data.success && data.conversations) setConversations(data.conversations); }
    } catch (e) { console.error('fetch conversations:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchStatusPreview = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/statuses/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) {
        const data = await res.json();
        const groups = (data.groups || []).slice(0, 6);
        setStatusGroups(groups);
        // ✅ Sync backend is_viewed into our local viewedStatusIds set
        // so rings immediately show grey for server-confirmed views
        const serverViewedIds = groups.flatMap(g =>
          g.statuses.filter(s => s.is_viewed).map(s => s.id)
        );
        if (serverViewedIds.length > 0) {
          setViewedStatusIds(prev => {
            const next = new Set([...prev, ...serverViewedIds]);
            try { localStorage.setItem('viewedStatusIds', JSON.stringify([...next])); } catch {}
            return next;
          });
        }
      }
    } catch (_) {}
  }, []);

  const fetchMyLikes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/my/likes/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) { const d = await res.json(); setMyLikes(d.likes || []); }
    } catch (_) {}
  }, []);

  const fetchMyFollowers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/my/followers/`, { headers: { Authorization: `Token ${getToken()}` } });
      if (res.ok) { const d = await res.json(); setMyFollowers(d.followers || []); }
    } catch (_) {}
  }, []);

  // ✅ Fetch I'm Selling unread count for the badge on the channel row
  const fetchImSellingUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/system-messages/`, {
        headers: { Authorization: `Token ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setImSellingUnread(data.unread_count || 0);
      }
    } catch (_) {}
  }, []);

  // ✅ FIX: Store stable refs to each fetch function so the useEffect below
  // only re-fires when activeTab actually changes — not on every re-render.
  // Without this, passing the callbacks directly as deps caused React to
  // detect new function identities on re-renders and re-fire all fetches,
  // generating the burst-of-6-users log noise every few seconds.
  const fetchConversationsRef  = useRef(fetchConversations);
  const fetchStatusPreviewRef  = useRef(fetchStatusPreview);
  const fetchMyLikesRef        = useRef(fetchMyLikes);
  const fetchMyFollowersRef    = useRef(fetchMyFollowers);
  const fetchImSellingUnreadRef = useRef(fetchImSellingUnread);
  useEffect(() => { fetchConversationsRef.current  = fetchConversations;  }, [fetchConversations]);
  useEffect(() => { fetchStatusPreviewRef.current  = fetchStatusPreview;  }, [fetchStatusPreview]);
  useEffect(() => { fetchMyLikesRef.current        = fetchMyLikes;        }, [fetchMyLikes]);
  useEffect(() => { fetchMyFollowersRef.current    = fetchMyFollowers;    }, [fetchMyFollowers]);
  useEffect(() => { fetchImSellingUnreadRef.current = fetchImSellingUnread; }, [fetchImSellingUnread]);

  // ✅ StrictMode guard — React mounts → unmounts → remounts in development,
  // causing this effect to fire twice on every tab switch.
  // The ref tracks the last tab we already fetched for so the second
  // StrictMode invocation is a no-op instead of a duplicate network burst.
  // NOTE: no cleanup reset — the ref persists across StrictMode remounts
  // intentionally. It only resets when activeTab genuinely changes value.
  const lastFetchedTabRef = useRef(null);

  useEffect(() => {
    if (activeTab !== 'chats' && activeTab !== 'unread') return;
    if (lastFetchedTabRef.current === activeTab) return; // already fetched — skip duplicate
    lastFetchedTabRef.current = activeTab;

    fetchConversationsRef.current();
    fetchStatusPreviewRef.current();
    fetchMyLikesRef.current();
    fetchMyFollowersRef.current();
    fetchImSellingUnreadRef.current(); // ✅ also refresh I'm Selling unread count
  }, [activeTab]); // ✅ only activeTab — no callback churn

  const handleRefresh = () => { fetchConversations(); onInboxRefresh?.(); };

  const handleMarkLikesSeen = useCallback(() => {
    localStorage.setItem('seenLikesCount', String(myLikes.length));
    setSeenLikesCount(myLikes.length);
  }, [myLikes.length]);

  const handleMarkFollowersSeen = useCallback(() => {
    localStorage.setItem('seenFollowersCount', String(myFollowers.length));
    setSeenFollowersCount(myFollowers.length);
  }, [myFollowers.length]);



  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ MEDIA UPLOAD HANDLER - Lines ~2760-2810
  // Validates media limits and updates validation state
  // ════════════════════════════════════════════════════════════════════════════════
  const handleStatusMediaUpload = useCallback((e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // ✅ VALIDATE MEDIA LIMITS
    const validation = validateMediaLimits(statusMedia, files);

    if (!validation.valid) {
      // ✅ Show error feedback
      setMediaValidation(prev => ({
        ...prev,
        error: validation.error,
        type: validation.type,
      }));
      
      // ✅ Show alert to user
      alert(validation.error);
      
      // ✅ Clear file input
      e.target.value = '';
      
      // ✅ Clear error after 5 seconds
      setTimeout(() => {
        setMediaValidation(prev => ({ ...prev, error: null }));
      }, 5000);
      
      return; // Stop upload
    }

    // ✅ FIX: Don't overwrite with local-only counts — just clear error
    // The useEffect on statusGroups keeps the base counts accurate
    setMediaValidation(prev => ({ ...prev, error: null }));

    // ✅ Process files and add to media
    const newMedia = Array.from(files).map(file => ({
      file,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));
    
    setStatusMedia(prev => [...prev, ...newMedia]);
  }, [statusMedia]);

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ REMOVE MEDIA HANDLER - Lines ~2810-2830
  // Remove individual media item and recalculate validation
  // ════════════════════════════════════════════════════════════════════════════════
  const handleRemoveMedia = useCallback((mediaId) => {
    setStatusMedia(prev => {
      const updated = prev.filter(m => m.id !== mediaId);
      // ✅ FIX: Don't reset to local-only counts — let the useEffect on statusGroups
      // handle the base counts. Only clear error here.
      setMediaValidation(prev => ({ ...prev, error: null }));
      return updated;
    });
  }, []);

  // ════════════════════════════════════════════════════════════════════════════════
  // ✅ CLEAR ALL MEDIA HANDLER - Lines ~2830-2845
  // Clear all media and reset validation
  // ════════════════════════════════════════════════════════════════════════════════
  const handleClearAllMedia = useCallback(() => {
    setStatusMedia([]);
    // ✅ FIX: Don't reset to zeros — let the useEffect on statusGroups recalculate
    setMediaValidation(prev => ({ ...prev, error: null }));
  }, []);

  const newLikesCount     = Math.max(0, myLikes.length     - seenLikesCount);
  const newFollowersCount = Math.max(0, myFollowers.length - seenFollowersCount);
  const groupedConversations = groupConversationsByUser(conversations, currentUserEmail);
  const unreadGrouped        = groupedConversations.filter(g => g.totalUnread > 0);
  // ✅ FIX: include I'm Selling unread in totalUnread so the Unread tab badge
  // and parent Messages tab notification count include system messages,
  // just like Likes and Followers already do.
  const totalUnread          = unreadGrouped.length + (imSellingUnread > 0 ? 1 : 0);

  const filtered = (activeTab === 'unread' ? unreadGrouped : groupedConversations).filter(g => {
    if (!search) return true;
    const name = g.otherUser?.username || g.otherUser?.email || '';
    return name.toLowerCase().includes(search.toLowerCase()) ||
      g.conversations.some(c => (c.ad?.title || '').toLowerCase().includes(search.toLowerCase()));
  });

  if (activeTab === 'status') return <StatusTab user={user} onBack={() => setActiveTab('chats')} />;
  if (channelView === 'imselling') return <IMSellingPanel onBack={() => { setChannelView(null); fetchImSellingUnread(); }} onUnreadChange={(count) => setImSellingUnread(count)} />;
  if (channelView === 'likes') return <LikesPanel myLikes={myLikes} onBack={() => setChannelView(null)} onMarkSeen={handleMarkLikesSeen} />;
  if (channelView === 'followers') return <FollowersPanel myFollowers={myFollowers} onBack={() => setChannelView(null)} onMarkSeen={handleMarkFollowersSeen} />;

  if (activeTab === 'settings') {
    return (
      <div className={styles.container}>
        <div className={styles.topBar}><h1 className={styles.topBarTitle}>Settings</h1><button className={styles.topBarMenu} onClick={() => setActiveTab('chats')}>←</button></div>
        <AccountSettings user={user} />
      </div>
    );
  }

  if (openConversation) {
    if (openConversation.type === 'direct_message') return <DirectMessageWindow conversation={openConversation} currentUser={user} onClose={() => { setOpenConversation(null); handleRefresh(); }} onRefresh={handleRefresh} />;
    return <OfferChatWindow offer={openConversation} currentUser={user} onClose={() => { setOpenConversation(null); handleRefresh(); }} onRefresh={handleRefresh} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <h1 className={styles.topBarTitle}>Messages</h1>
        <button className={styles.topBarMenu} onClick={handleRefresh} title="Refresh">↻</button>
      </div>

      {/* Status strip */}
      <div style={{ overflowX: 'auto', padding: '12px 16px 8px', borderBottom: '1px solid #e9edef', background: '#f8fafc', display: 'flex', gap: 16, alignItems: 'flex-start', scrollbarWidth: 'none' }}>
        {(() => {
          const myStatusGroup = statusGroups.find(g => (g.user.email || '').toLowerCase() === currentUserEmail);
          const rawMyThumb = myStatusGroup?.statuses[0]?.thumbnail_url || myStatusGroup?.statuses[0]?.media_url;
          const myThumb = resolveMediaUrl(rawMyThumb);
          return (
            <div onClick={() => setActiveTab('status')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer', minWidth: 60 }}>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative', width: 54, height: 54 }}>
                  <div style={{ position: 'absolute', top: 4, left: 4, width: 46, height: 46, borderRadius: '50%', overflow: 'hidden', background: avatarColor(user?.username || 'U'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                    {myThumb
                      ? (myStatusGroup?.statuses[0]?.media_type === 'video' || /\.(mp4|mov|webm|avi)$/i.test(myThumb || '')
                          ? <VideoThumb src={myThumb} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <img src={myThumb} alt="My status" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />)
                      : getInitials(user?.username || user?.identifier || 'U')
                    }
                  </div>
                  <SegmentedRing count={myStatusGroup ? myStatusGroup.statuses.length : 0} viewed={0} size={54} strokeWidth={3} />
                </div>
                <div
                  onPointerDown={e => { e.stopPropagation(); e.preventDefault(); setShowUploader(true); }}
                  title="Add a new status"
                  style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: '50%', background: '#25D366', border: '2.5px solid #f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1, zIndex: 10, cursor: 'pointer' }}>+</div>
              </div>
              <span style={{ color: '#54656f', fontSize: 11, textAlign: 'center', maxWidth: 60 }}>My Status</span>
            </div>
          );
        })()}
        {statusGroups.filter(g => (g.user.email || '').toLowerCase() !== currentUserEmail).map(g => {
          const name      = g.user.username || g.user.email;
          // ✅ Prefer thumbnail_url (a still image) over media_url.
          // For videos, media_url is a .mp4 — browsers can't render that as <img>.
          // thumbnail_url is a pre-generated still frame from the backend.
          const firstStatus = g.statuses[0];
          const thumbUrl  = resolveMediaUrl(firstStatus?.thumbnail_url);
          const mediaUrl  = resolveMediaUrl(firstStatus?.media_url);
          const isVideo   = firstStatus?.media_type === 'video' ||
                            /\.(mp4|mov|webm|avi)$/i.test(mediaUrl || '');
          // Use thumbnail_url for images and videos.
          // If no thumbnail, fall back to media_url only for images.
          // ✅ For videos: use thumbnail_url if available, else fall back to
          // the raw mediaUrl rendered as a muted <video> (shows first frame).
          // For images: use thumbnail_url, then mediaUrl.
          const stillUrl   = thumbUrl || (!isVideo ? mediaUrl : null);
          const videoSrc   = isVideo ? mediaUrl : null; // only used when no still
          const hasPreview = !!(stillUrl || videoSrc);
          return (
            <div key={g.user.id} onClick={() => setShowingStatus(g)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer', minWidth: 60 }}>
              <div style={{ position: 'relative', width: 54, height: 54 }}>
                <div style={{
                  position: 'absolute', top: 4, left: 4, width: 46, height: 46,
                  borderRadius: '50%', overflow: 'hidden',
                  background: hasPreview ? '#111' : avatarColor(name),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: 20,
                }}>
                  {stillUrl ? (
                    <img
                      src={stillUrl}
                      alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : videoSrc ? (
                    // ✅ Canvas-based first-frame capture — no black flash
                    <VideoThumb
                      src={videoSrc}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      fallback={<span style={{ fontSize: 20, color: '#fff', fontWeight: 700 }}>{getInitials(name)}</span>}
                    />
                  ) : (
                    getInitials(name)
                  )}
                </div>
                <SegmentedRing
                  count={g.statuses.length}
                  viewed={g.statuses.filter(s => s.is_viewed || viewedStatusIds.has(s.id)).length}
                  size={54}
                  strokeWidth={3}
                />
              </div>
              <span style={{ color: '#54656f', fontSize: 11, textAlign: 'center', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name.length > 9 ? name.slice(0, 9) + '…' : name}</span>
            </div>
          );
        })}
        <div onClick={() => setActiveTab('status')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer', minWidth: 60 }}>
          <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#e9edef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>👁</div>
          <span style={{ color: '#54656f', fontSize: 11, textAlign: 'center' }}>See all</span>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchRow}>
        <div className={styles.searchBox}>
          <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input className={styles.searchInput} placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button className={`${styles.tabBtn} ${activeTab === 'chats'    ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('chats')}>All Chats</button>
        <button className={`${styles.tabBtn} ${activeTab === 'unread'   ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('unread')}>
          Unread {totalUnread > 0 && <span className={styles.tabBadge}>{totalUnread}</span>}
        </button>
        <button className={`${styles.tabBtn} ${activeTab === 'status'   ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('status')}>📷 Status</button>
        <button className={`${styles.tabBtn} ${activeTab === 'settings' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('settings')}>⚙️</button>
      </div>

      {/* Chat list */}
      <div className={styles.chatList}>
        {activeTab === 'chats' && (
          <>
            <div className={styles.chatRow} onClick={() => setChannelView('imselling')} style={{ cursor: 'pointer' }}>
              <div className={styles.chatRowAvatar}><div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏪</div></div>
              <div className={styles.chatRowBody}>
                <div className={styles.chatRowTop}><span className={styles.chatRowName} style={{ fontWeight: 700 }}>I&apos;m Selling</span><span className={styles.chatRowTime}>Now</span></div>
                <div className={styles.chatRowBottom}>
                  <span className={styles.chatRowPreview}>
                    {imSellingUnread > 0 ? `${imSellingUnread} new message${imSellingUnread > 1 ? 's' : ''}` : 'Announcements & updates from the team'}
                  </span>
                  {imSellingUnread > 0 && <span className={styles.unreadBadge}>{imSellingUnread > 99 ? '99+' : imSellingUnread}</span>}
                </div>
              </div>
            </div>
            <div className={styles.chatRow} onClick={() => setChannelView('likes')} style={{ cursor: 'pointer' }}>
              <div className={styles.chatRowAvatar}><div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#f43f5e,#fb923c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>❤️</div></div>
              <div className={styles.chatRowBody}>
                <div className={styles.chatRowTop}><span className={styles.chatRowName} style={{ fontWeight: 700 }}>Likes</span><span className={styles.chatRowTime}>{myLikes.length > 0 ? formatTime(myLikes[0]?.liked_at) : ''}</span></div>
                <div className={styles.chatRowBottom}>
                  <span className={styles.chatRowPreview}>{myLikes.length === 0 ? 'See who liked your listings' : `${maskLikerEmail(myLikes[0]?.liker_username || myLikes[0]?.liker_email)} liked ${myLikes[0]?.ad_title || 'your listing'}`}</span>
                  {newLikesCount > 0 && <span className={styles.unreadBadge}>{newLikesCount > 99 ? '99+' : newLikesCount}</span>}
                </div>
              </div>
            </div>
            <div className={styles.chatRow} onClick={() => setChannelView('followers')} style={{ cursor: 'pointer' }}>
              <div className={styles.chatRowAvatar}><div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>👥</div></div>
              <div className={styles.chatRowBody}>
                <div className={styles.chatRowTop}><span className={styles.chatRowName} style={{ fontWeight: 700 }}>Followers</span><span className={styles.chatRowTime}>{myFollowers.length > 0 ? formatTime(myFollowers[0]?.followed_at) : ''}</span></div>
                <div className={styles.chatRowBottom}>
                  <span className={styles.chatRowPreview}>{myFollowers.length === 0 ? 'See who started following you' : `${myFollowers[0]?.follower_username || myFollowers[0]?.follower_email || 'Someone'} started following you`}</span>
                  {newFollowersCount > 0 && <span className={styles.unreadBadge}>{newFollowersCount > 99 ? '99+' : newFollowersCount}</span>}
                </div>
              </div>
            </div>
            <div style={{ height: 1, background: '#e9edef', margin: '4px 0' }} />
          </>
        )}
        {loading ? (
          <div className={styles.loadingState}>
            {[1,2,3,4].map(i => (
              <div key={i} className={styles.skeletonRow}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonLines}>
                  <div className={styles.skeletonLine} style={{ width: '60%' }} />
                  <div className={styles.skeletonLine} style={{ width: '80%', opacity: 0.5 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <p className={styles.emptyTitle}>{activeTab === 'unread' ? 'No unread messages' : 'No conversations yet'}</p>
            <p className={styles.emptySub}>{activeTab === 'unread' ? "You're all caught up!" : 'Make an offer or message a seller to start chatting'}</p>
          </div>
        ) : filtered.map(g => (
          <ConversationRow key={g.otherEmail} groupedConv={g} currentUserEmail={currentUserEmail}
            onClick={gc => gc.conversations.length === 1 ? setOpenConversation(gc.conversations[0]) : setShowConversationList(gc)}
            isUnread={g.totalUnread > 0} />
        ))}
      </div>

      {showConversationList && (
        <ConversationListModal groupedConv={showConversationList} currentUserEmail={currentUserEmail}
          onSelectConversation={conv => { setShowConversationList(null); setOpenConversation(conv); }}
          onClose={() => setShowConversationList(null)} />
      )}
      {showingStatus && (
        <StatusViewer group={showingStatus} currentUserEmail={currentUserEmail}
          onClose={() => { setShowingStatus(null); fetchStatusPreview(); }}
          onDeleted={fetchStatusPreview}
          onViewed={handleStatusViewed} />
      )}
      {showUploader && (
        <StatusUploader
          onClose={() => setShowUploader(false)}
          onUploaded={() => { setShowUploader(false); fetchStatusPreview(); }}
          // ✅ PASS the validation states and handlers
          statusMedia={statusMedia}
          setStatusMedia={setStatusMedia}
          mediaValidation={mediaValidation}
          setMediaValidation={setMediaValidation}
          onMediaUpload={handleStatusMediaUpload}
          onRemoveMedia={handleRemoveMedia}
          onClearAllMedia={handleClearAllMedia}
        />
      )}
    </div>
  );
};

export default MessagesTab;




